import { account } from './account.js';
import { PlayerCore } from './player.js';
import { ads } from './ads.js';
import { catalog } from './catalog.js';
import { epg, programProgress } from './epg.js';
import { imageUrl, programMeta } from '../ui/render.js';
import { splitProgramDesc } from './epg-desc.js';
import { settings, storage } from './storage.js';
import { device } from './device.js';
import { tvnav } from './tvnav.js';
import { t } from './i18n.js';
import { notify } from './notify.js';
import { escapeHtml, formatDuration, formatClock, cleanName, Emitter, throttle } from './utils.js';
import { pickVariant, lowerVariant, shouldDowngrade, variantLabel, needKbps } from './variants.js';
import { transcodeAvailable, shouldTranscode, transcodeUrl, localTranscodeAvailable, localTranscodeLastError } from './transcode.js';
import { offline, Offline } from './offline.js';
import { parseSubtitles, cuesAt, cueHtml, subtitleStore } from './subtitles.js';
import { formatDate } from './utils.js';
import { i18n } from './i18n.js';
import { SeekPreview, previewAllowed } from './seek-preview.js';
import { stripCountry } from './country.js';
import { presetOf } from './presets.js';
import { openReport } from '../pages/report.js';
import { normLang, trackLang, findTrack, getTrackPrefs, rememberTracks, getIntro, setIntro, clearIntro } from './series-prefs.js';

/* Programas con grabación programada o en curso (clave canal:fin), para marcarlos en la guía lateral. */
const scheduledRecs = new Set();

/* Reproductor a pantalla completa (overlay único, independiente del router).
   Soporta tres modos: 'live' (canal, con zapping, EPG y catch-up), 'vod' (película) y
   'episode' (episodio con "siguiente episodio" automático). */

const IDLE_MS = 3500;
const PROGRESS_SAVE_MS = 5000;
const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

// Idiomas que se ofrecen al buscar subtítulos (códigos de OpenSubtitles).
export const SUB_LANGS = [['es', 'Español'], ['en', 'English'], ['ca', 'Català'], ['gl', 'Galego'], ['eu', 'Euskara'], ['fr', 'Français'], ['it', 'Italiano'], ['pt-pt', 'Português'], ['de', 'Deutsch']];
const subLangName = (code) => (SUB_LANGS.find((l) => l[0] === code) || [code, String(code).toUpperCase()])[1];

export class PlayerUI extends Emitter {
    constructor() {
        super();
        this.root = null;
        this.core = null;
        this.mode = null;
        this.item = null;
        this.series = null;
        this.episode = null;
        this.channelList = [];
        this.channelIndex = -1;
        this.catchup = null;
        this.isOpen = false;
        this.idleTimer = null;
        this.progressTimer = null;
        this.uiTimer = null;
        this.wakeLock = null;
        this.digits = '';
        this.digitTimer = null;
        this.nextCountdown = null;
        this.side = null;
        this.fit = 'contain';
        this.errorShown = false;
        this._build();
    }

    /* ---------------- Construcción del DOM ---------------- */

    _build() {
        const root = document.createElement('div');
        root.className = 'player';
        root.id = 'player';
        root.setAttribute('role', 'dialog');
        root.setAttribute('aria-label', 'Player');
        root.innerHTML = `
            <video playsinline webkit-playsinline preload="auto" crossorigin="anonymous" poster="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="></video>
            <div class="player-subs" data-el="subs" aria-hidden="true"></div>
            <div class="ad-layer" data-el="adlayer" data-nav-skip hidden></div>
            <div class="player-top">
                <button class="pbtn" data-action="close" aria-label="${t('common.back')}"><span class="mi">arrow_back</span></button>
                <div class="player-title"><h2 data-el="title"></h2><p data-el="subtitle"></p></div>
                <button class="pbtn pbtn-fav" data-action="fav" data-mode="live" data-el="favbtn" aria-label="${t('common.favoriteAdd')}"><span class="mi">favorite_border</span></button>
                <button class="pbtn" data-action="info" aria-label="${t('player.info')}"><span class="mi">info</span></button>
                <button class="pbtn" data-action="channels" data-mode="live" aria-label="${t('player.channels')}"><span class="mi">list</span></button>
                <button class="pbtn" data-action="episodes" data-mode="episode" aria-label="${t('common.episodes')}"><span class="mi">view_list</span></button>
                <button class="pbtn pbtn-record" data-action="record" data-mode="live" data-el="recbtn" aria-label="${t('rec.record')}" hidden><span class="mi">fiber_manual_record</span></button>
                <button class="pbtn" data-action="settings" aria-label="${t('player.settings')}"><span class="mi">tune</span></button>
            </div>
            <div class="player-quality" data-el="quality" hidden></div>
            <div class="player-center">
                <div class="spinner" data-el="spinner" hidden></div>
                <pre class="player-stats" data-el="stats" hidden></pre>
                <button class="player-bigplay" data-action="toggle" data-el="bigplay" hidden aria-label="${t('common.play')}"><span class="mi">play_arrow</span></button>
            </div>
            <div class="player-flash" data-el="flash"></div>
            <div class="player-chnum" data-el="chnum"></div>
            <div class="player-info" data-el="info"></div>
            <div class="player-next" data-el="next"></div>
            <button type="button" class="player-skip" data-el="skip" hidden></button>
            <div class="player-error" data-el="error" hidden>
                <span class="mi">error_outline</span>
                <h3 data-el="errortitle">${t('player.errorTitle')}</h3>
                <p data-el="errortext"></p>
                <div class="flex">
                    <button class="btn btn-primary" data-action="retry"><span class="mi">refresh</span>${t('common.retry')}</button>
                    <button class="btn" data-action="close">${t('common.close')}</button>
                </div>
            </div>
            <div class="player-layer player-controls" data-el="controls">
                <div class="player-seek" data-el="seek" tabindex="0" role="slider" aria-label="Seek">
                    <div class="player-seek-track">
                        <div class="player-seek-buffer" data-el="buffer"></div>
                        <div class="player-seek-fill" data-el="fill"></div>
                        <div class="player-seek-marks" data-el="marks"></div>
                        <div class="player-seek-thumb" data-el="thumb"></div>
                    </div>
                    <div class="player-seek-tooltip" data-el="tooltip"><div class="player-seek-preview" data-el="preview" hidden></div><span data-el="tooltiptime"></span></div>
                </div>
                <div class="player-buttons">
                    <button class="pbtn" data-action="prev" data-mode="live episode" aria-label="${t('live.prevChannel')}"><span class="mi">skip_previous</span></button>
                    <button class="pbtn" data-action="toggle" data-el="playbtn" data-nav-default aria-label="${t('common.play')}"><span class="mi">pause</span></button>
                    <button class="pbtn" data-action="next" data-mode="live episode" aria-label="${t('live.nextChannel')}"><span class="mi">skip_next</span></button>
                    <button class="pbtn" data-action="rew" data-mode="vod episode" aria-label="-10s"><span class="mi">replay_10</span></button>
                    <button class="pbtn" data-action="ffw" data-mode="vod episode" aria-label="+10s"><span class="mi">forward_10</span></button>
                    <div class="player-volume">
                        <button class="pbtn" data-action="mute" data-el="mutebtn" aria-label="Mute"><span class="mi">volume_up</span></button>
                        <input type="range" min="0" max="100" value="100" data-el="volume" aria-label="Volume"/>
                    </div>
                    <span class="player-time" data-el="time"></span>
                    <button class="pbtn pbtn-label pbtn-live" data-action="golive" data-el="livebtn" data-mode="live"><span class="live-dot"></span><span data-el="livetext">${t('common.live')}</span></button>
                    <span class="spacer"></span>
                    <button class="pbtn pbtn-label hide-mobile" data-action="speed" data-el="speedbtn" data-mode="vod episode">1x</button>
                    <button class="pbtn" data-action="nextep" data-mode="episode" aria-label="${t('series.nextEpisode')}"><span class="mi">skip_next</span></button>
                    <button class="pbtn" data-action="tracks" aria-label="${t('player.audio')} / ${t('player.subtitles')}"><span class="mi">subtitles</span></button>
                    <button class="pbtn hide-mobile" data-action="pip" data-el="pipbtn" aria-label="${t('player.pip')}"><span class="mi">picture_in_picture_alt</span></button>
                    <button class="pbtn" data-action="fullscreen" aria-label="${t('player.fullscreen')}"><span class="mi" data-el="fsicon">fullscreen</span></button>
                </div>
            </div>
            <div class="player-side" data-el="side" data-nav-skip>
                <div class="player-side-head">
                    <h3 data-el="sidetitle"></h3>
                    <button class="pbtn" data-action="closeside" aria-label="${t('common.close')}"><span class="mi">close</span></button>
                </div>
                <div class="player-side-body" data-el="sidebody"></div>
            </div>
        `;
        document.body.appendChild(root);
        this.root = root;
        this.el = {};
        root.querySelectorAll('[data-el]').forEach((el) => { this.el[el.getAttribute('data-el')] = el; });
        // «Mi lista» del canal que suena: el corazón se pone al día al abrir, al cambiar de canal y al marcarlo desde cualquier sitio.
        this.on('open', () => this._syncFav());
        this.on('change', () => this._syncFav());
        catalog.on('favorites', () => this._syncFav());
        this.video = root.querySelector('video');
        this.core = new PlayerCore(this.video);
        this._bindEvents();
    }

    _bindEvents() {
        const root = this.root;
        this.el.skip.addEventListener('click', (e) => { e.stopPropagation(); this._doSkip(); });
        this.el.adlayer.addEventListener('click', (e) => e.stopPropagation());
        this.el.adlayer.addEventListener('dblclick', (e) => e.stopPropagation());
        root.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-action]');
            const touchUi = device.hasTouch && !device.isTV;
            if (btn) {
                e.stopPropagation();
                this._action(btn.getAttribute('data-action'), btn);
                this._wake(touchUi);
                return;
            }
            if (e.target === this.video || e.target.classList.contains('player-center')) {
                if (touchUi) {
                    // Táctil: un toque muestra los controles y se quedan hasta el siguiente toque;
                    // doble toque en el tercio izquierdo/derecho salta 10 s (como YouTube/Netflix).
                    const now = Date.now();
                    const x = e.clientX;
                    if (now - (this._lastTap || 0) < 350 && Math.abs(x - this._lastTapX) < 80) {
                        clearTimeout(this._tapTimer);
                        this._lastTap = 0;
                        const w = root.clientWidth;
                        if (this.mode !== 'live' || this.catchup || this.core.dvrWindow() > 60) {
                            if (x < w * 0.35) { this.core.seekBy(-10); this._flash('replay_10'); return; }
                            if (x > w * 0.65) { this.core.seekBy(10); this._flash('forward_10'); return; }
                        }
                        this.core.toggle();
                        this._wake(true);
                        return;
                    }
                    this._lastTap = now;
                    this._lastTapX = x;
                    clearTimeout(this._tapTimer);
                    this._tapTimer = setTimeout(() => {
                        this.root.classList.contains('is-idle') ? this._wake(true) : this._sleep(true);
                    }, 350);
                } else {
                    this.core.toggle();
                    this._wake();
                }
            }
        });
        root.addEventListener('dblclick', (e) => {
            if (e.target === this.video) this._action('fullscreen');
        });
        // Los toques generan también eventos de ratón de compatibilidad; en táctil se ignoran.
        root.addEventListener('mousemove', throttle(() => { if (!(device.hasTouch && !device.isTV)) this._wake(); }, 300));
        // En táctil el toque se gestiona en 'click' (mostrar/ocultar); despertar aquí lo anularía.

        // Barra de progreso: ratón, táctil y teclado
        const seek = this.el.seek;
        const pointerPos = (e) => {
            const rect = seek.getBoundingClientRect();
            const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
            return Math.max(0, Math.min(1, x / rect.width));
        };
        let dragging = false;
        this.preview = new SeekPreview(this.el.preview);
        const onMove = (e) => {
            const p = pointerPos(e);
            this.el.tooltiptime.textContent = this._formatSeekTime(p);
            if (this.preview.available && this.core.duration > 0) this.preview.request(this.core.duration * p, this.core.duration);
            // El globo sigue al puntero sin salirse de la barra por los lados.
            const rect = seek.getBoundingClientRect();
            const half = Math.min(this.el.tooltip.offsetWidth / 2, rect.width / 2);
            this.el.tooltip.style.left = Math.max(half, Math.min(rect.width - half, p * rect.width)) + 'px';
            if (dragging) {
                this._previewSeek(p);
                // Mientras se arrastra, los controles no se esconden.
                this._wake();
            }
        };
        seek.addEventListener('pointerdown', (e) => {
            dragging = true;
            seek.classList.add('is-dragging');
            seek.setPointerCapture(e.pointerId);
            onMove(e);
        });
        seek.addEventListener('pointermove', (e) => {
            seek.classList.add('is-hover');
            onMove(e);
        });
        seek.addEventListener('pointerleave', () => seek.classList.remove('is-hover'));
        seek.addEventListener('pointerup', (e) => {
            if (!dragging) return;
            dragging = false;
            seek.classList.remove('is-dragging');
            this._commitSeek(pointerPos(e));
        });
        seek.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault();
                e.stopPropagation();
                this.core.seekBy(e.key === 'ArrowLeft' ? -10 : 10);
                this._flash(e.key === 'ArrowLeft' ? 'replay_10' : 'forward_10');
                this._wake();
            }
        });
        seek.addEventListener('nav:focus', () => seek.classList.add('is-focused'));
        seek.addEventListener('blur', () => seek.classList.remove('is-focused'));

        this.el.volume.addEventListener('input', (e) => {
            const v = Number(e.target.value) / 100;
            this.core.volume = v;
            this.core.muted = v === 0;
            settings.set('volume', v);
        });

        // Eventos del núcleo
        const c = this.core;
        c.on('play', () => this._renderPlayState());
        c.on('pause', () => { this._renderPlayState(); this._wake(true); });
        c.on('playing', () => {
            if (this._loadAt && !this._loadTimed) {
                this._loadTimed = true;
                const ms = Math.round(performance.now() - this._loadAt);
                this.lastStartupMs = ms;
                this.startups = (this.startups || []).concat(ms).slice(-20);
                console.info('[player] arranque ' + ms + ' ms (' + this.mode + ')');
            }
            this._spinner(false);
            this._onStallEnd();
            this._renderPlayState();
            this.errorShown = false;
            this._stopWatchdog();
            // Si el aviso de "no arranca" saltó pero al final los datos llegaron, se retira solo.
            if (!this.el.error.hidden) { this.el.error.hidden = true; this._renderPlayState(); }
        });
        c.on('waiting', () => { this._spinner(true); this._onStallStart(); });
        c.on('canplay', () => this._spinner(false));
        c.on('seeking', () => { this._spinner(true); this._seekAt = Date.now(); });
        c.on('seeked', () => this._spinner(false));
        c.on('timeupdate', () => { this._renderTime(); this._renderSubs(); this._maybeMidroll(); this._renderSkip(); this._maybeOfferNext(); });
        c.on('seeked', () => this._renderSubs());
        c.on('progress', () => { this._renderTime(); this._watchdogProgress = Date.now(); });
        c.on('loadedmetadata', () => { this._watchdogProgress = Date.now(); });
        c.on('canplay', () => { this._watchdogProgress = Date.now(); });
        c.on('durationchange', () => this._renderTime());
        c.on('loadedmetadata', () => { this._renderTime(); this._renderQuality(); });
        c.on('volumechange', () => this._renderVolume());
        c.on('ratechange', () => { this.el.speedbtn.textContent = this.core.playbackRate + 'x'; });
        c.on('levels', () => this._renderQuality());
        c.on('levelchange', () => this._renderQuality());
        c.on('ended', () => this._onEnded());
        c.on('error', (err) => this._onError(err));
        c.on('retrying', () => { this._spinner(true); this._flash('sync', t('player.retrying')); });
        c.on('autoplayblocked', () => { this.el.bigplay.hidden = false; this._spinner(false); });

        document.addEventListener('fullscreenchange', () => this._renderFullscreen());
        document.addEventListener('webkitfullscreenchange', () => this._renderFullscreen());
        // Imagen en imagen: el reproductor se aparta para seguir usando la app con el vídeo flotando;
        // al salir del modo PiP (o al pulsar la pastilla "Volver al reproductor") vuelve a pantalla.
        this.video.addEventListener('enterpictureinpicture', () => { this.el.pipbtn.classList.add('is-active'); this._minimize(); });
        this.video.addEventListener('leavepictureinpicture', () => { this.el.pipbtn.classList.remove('is-active'); if (this.minimized) this._restore(); });
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible' && this.isOpen) this._requestWakeLock();
        });
        // Recarga (F5) o cierre de pestaña mientras se reproduce: guardar la posición al instante.
        window.addEventListener('pagehide', () => { if (this.isOpen) this._saveProgress(true); });
    }

    /* ---------------- API pública ---------------- */

    /**
     * open({ mode:'live', item: channel, list: channels[] })
     * open({ mode:'vod', item: movie, startPosition })
     * open({ mode:'episode', series, episode, startPosition })
     * open({ mode:'live', item: channel, catchup: program })
     */
    async open(opts) {
        // La demo pública enseña los canales (lista y guía) pero no los reproduce: solo «Cine clásico». El servidor también lo impide (proxy de la demo).
        if (opts && opts.mode === 'live' && account.demo) {
            notify.toast(t('demo.liveBlocked'), { icon: 'live_tv', duration: 4500 });
            return;
        }
        if (this.minimized) this._restore(true);
        this.mode = opts.mode;
        this.item = opts.item || null;
        this.series = opts.series || null;
        this.episode = opts.episode || null;
        this.catchup = opts.catchup || null;
        this.forceTranscode = null;
        this._triedTranscode = false;
        this._transcodeRetries = 0;
        this._retryPending = false;
        this._adKey = '';
        this._segments = [];
        this._segSeq = (this._segSeq || 0) + 1;
        this._adFree = false;
        this._midAt = null;
        this._routeKey = ''; // sesión nueva: los proveedores probados en la anterior no cuentan (si no, reabrir el mismo título fallaba)
        this.channelList = opts.list || (this.mode === 'live' ? catalog.visible('live') : []);
        this.channelIndex = this.mode === 'live' ? this.channelList.findIndex((c) => c.id === this.item.id) : -1;
        // El canal que suena tiene que estar en su lista (puede venir de «Continuar en directo», de una búsqueda o de otro proveedor y no estar
        // en la lista de su categoría): si no, la lista de canales se abría en el primero y no en el que estás viendo.
        if (this.mode === 'live' && this.item && this.channelIndex === -1) {
            this.channelList = [this.item].concat(this.channelList);
            this.channelIndex = 0;
        }
        this._resetVariants();
        this.errorShown = false;
        this.el.error.hidden = true;
        this.el.next.classList.remove('is-open');
        this.el.info.classList.remove('is-open');
        this._closeSide();
        this._cancelNextCountdown();

        this.root.classList.add('is-open');
        this.root.setAttribute('data-mode', this.mode);
        this.root.querySelectorAll('[data-mode]').forEach((el) => {
            const modes = el.getAttribute('data-mode').split(' ');
            el.hidden = modes.indexOf(this.mode) === -1;
        });
        this.isOpen = true;
        document.documentElement.classList.add('player-open');
        this.emit('open');
        this._wake();
        this._requestWakeLock();
        // La app (main.js) ya resuelve la capa activa teniendo en cuenta el reproductor; solo si no hay resolver se
        // pone uno propio. Antes se pisaba el de la app y al cerrar se dejaba en null: desde entonces ningún diálogo,
        // menú ni popup contaba como capa y el mando no podía entrar en ellos.
        if (!tvnav.layerResolver) {
            this._ownResolver = () => (this.side ? this.el.side : this.root);
            tvnav.layerResolver = this._ownResolver;
        }
        tvnav.onKey = (e, action) => this._onNavKey(e, action);
        tvnav.onEnter = (e, target) => this._onEnter(e, target);
        if (device.isTV) setTimeout(() => tvnav.focusLayer(this.root, this.el.playbtn), 50);

        await this._loadCurrent(opts.startPosition || 0);
    }

    async _loadCurrent(startPosition) {
        this._prepareNext();
        this._loadAt = performance.now(); // para medir cuánto tarda en verse imagen (estadísticas)
        this._loadTimed = false;
        this._startWanted = startPosition;
        this._spinner(true);
        this.el.bigplay.hidden = true;
        this.el.error.hidden = true;
        const volume = settings.get('volume');
        if (typeof volume === 'number') this.core.volume = volume;

        // Lo descargado en el dispositivo se ve aunque la suscripción haya caducado; lo demás, no.
        const local = this.mode === 'live' ? '' : await this._openLocal();
        const route = local ? null : await this._pickRoute();
        if (local) this.route = null;
        if (!local && !route) return;

        let url;
        let live = false;
        let transcode = null;
        if (this.mode === 'live') {
            if (!this.catchup) this._applyStartVariant();
            url = this.catchup ? catalog.catchupUrl(this.item, this.catchup) : catalog.liveUrl(route.target);
            live = !this.catchup;
            catalog.touchRecent(this.item.id);
        } else if (local) {
            // Descargado en este dispositivo: se reproduce desde ahí, sin red ni conversión.
            url = local;
        } else {
            // Vídeo bajo demanda: si el audio es Dolby/DTS y este dispositivo no lo decodifica,
            // se pide por /transcode (audio → AAC en el servidor).
            let info;
            let raw;
            if (this.mode === 'vod') {
                url = catalog.vodUrl(route.target);
                raw = catalog.rawVodUrl(route.target);
                info = (await catalog.routeInfo('vod', route.target).catch(() => null)) || this.item;
            } else {
                url = catalog.episodeUrl(route.target);
                raw = catalog.rawEpisodeUrl(route.target);
                info = route.target;
            }
            const want = this.forceTranscode != null ? (this.forceTranscode && transcodeAvailable()) : shouldTranscode(info, raw);
            if (want) transcode = { urlFor: (t) => transcodeUrl(raw, t), duration: Number(info.durationSecs) || 0 };
        }
        if (!local && !(await this._preroll(startPosition, route))) return;
        this._loadSegments();
        const limit = route ? catalog.subscriptionOfSource(route.pid).max : 0;
        this.preview.setSource(!transcode && previewAllowed({ enabled: settings.get('seekPreview') !== false, tv: device.isTV, live: this.mode === 'live', url, local: !!local, max: limit }) ? url : '');
        this._renderTitle();
        this._renderInfo();
        this._renderTime();
        this._renderRecordButton();
        this._restoreSubs();
        this._scheduleTrackPrefs();
        this.el.speedbtn.textContent = '1x';
        this.core.playbackRate = 1;
        this._stopProgressTimer();
        if (this.mode !== 'live') this._startProgressTimer();
        this._startWatchdog(!!transcode);
        if (transcode && !(await this._checkTranscode(transcode.urlFor(startPosition)))) return;
        try {
            await this.core.load({ url, live, startPosition, muted: false, autoplay: true, transcode });
        } catch (err) {
            this._onError({ type: 'generic', fatal: true, message: err && err.message });
        }
    }

    /**
     * De qué proveedor se va a reproducir. Un título puede estar en varios: se empieza por el suyo
     * y, si falla o tiene la suscripción caducada, se pasa al siguiente (ver _onError). Con todos
     * caducados no se pide el vídeo: el panel lo rechazaría y saldría un error de formato que no
     * explica nada. Antes de darlo por caducado se vuelve a preguntar al panel, por si se acaba
     * de renovar. Devuelve { pid, name, target } o null si ya se ha mostrado el motivo.
     */
    async _pickRoute() {
        const subject = this.mode === 'episode' ? this.series : this.item;
        if (this.mode === 'vod' && /^rec:/.test(String(subject.id))) return { pid: '', key: 'rec', name: '', target: subject }; // grabaciones de Filoteca
        const key = this.mode + ':' + (this.mode === 'episode' ? this.episode.id : subject.id);
        if (this._routeKey !== key) {
            this._routeKey = key;
            this._routeTried = [];
        }
        let routes = catalog.playRoutes(subject).filter((r) => this._routeTried.indexOf(r.key) === -1);
        if (this.catchup) routes = routes.filter((r) => r.target === subject); // el archivo es del canal de su proveedor
        if (routes.length && routes[0].blocked && routes[0].pid === catalog.id) {
            // Todo lo que queda está caducado. Puede que el principal se acabe de renovar.
            await catalog.checkSubscription().catch(() => {});
            routes = catalog.playRoutes(subject).filter((r) => this._routeTried.indexOf(r.key) === -1);
        }
        while (routes.length) {
            const route = routes.shift();
            if (route.blocked) {
                const sub = catalog.subscriptionOfSource(route.pid);
                this._onError({ type: sub.state, sub, name: route.name, final: true });
                return null;
            }
            this._routeTried.push(route.key);
            this.route = route;
            if (this.mode !== 'episode' || route.target === subject) {
                if (this.mode === 'episode') route.target = this.episode;
                return route;
            }
            // Serie de otro proveedor: hay que encontrar allí el mismo episodio.
            const ep = await catalog.altEpisode(this.episode, route.target).catch(() => null);
            if (ep) {
                route.target = ep;
                return route;
            }
        }
        this._onError({ type: 'generic', fatal: true, final: true });
        return null;
    }

    /** ¿Queda otro proveedor con este título por probar? */
    _nextRoute() {
        const subject = this.mode === 'episode' ? this.series : this.item;
        if (!subject || this.catchup || this.local) return null;
        return catalog.playRoutes(subject).find((r) => !r.blocked && (this._routeTried || []).indexOf(r.key) === -1) || null;
    }

    /** URL local si lo que se va a reproducir está descargado en el dispositivo; si no, ''. */
    async _openLocal() {
        this._releaseLocal();
        const target = this.mode === 'vod' ? this.item : this.episode;
        if (!target || !offline.supported) return '';
        const local = await offline.open(Offline.key(catalog.id, this.mode === 'vod' ? 'vod' : 'episode', target.id));
        if (!local) return '';
        this.local = local;
        return local.url;
    }

    _releaseLocal() {
        if (this.local) {
            try { this.local.release(); } catch (e) { /* ya liberada */ }
            this.local = null;
        }
    }

    /** Antes de pedir el flujo convertido, HEAD al servidor: sin sesión o sin hueco, aviso claro
        en lugar de un "formato no compatible" del <video>. */
    async _checkTranscode(url) {
        let status = 0;
        try {
            const r = await fetch(url, { method: 'HEAD', cache: 'no-store' });
            if (r.ok) return true;
            status = r.status;
        } catch (e) {
            return true; // sin respuesta clara (CORS, red): se intenta reproducir igualmente
        }
        this._onError({ type: 'transcode', status, fatal: true });
        return false;
    }

    /* ---------------- Modo apartado (imagen en imagen) ---------------- */

    _minimize() {
        if (!this.isOpen || this.minimized) return;
        this.minimized = true;
        this._closeSide();
        this.root.classList.add('is-minimized');
        document.documentElement.classList.remove('player-open');
        if (!this.mini) {
            this.mini = document.createElement('button');
            this.mini.className = 'player-mini';
            this.mini.type = 'button';
            this.mini.addEventListener('click', () => this._restore(true));
            document.body.appendChild(this.mini);
        }
        const title = (this.el.title && this.el.title.textContent.trim()) || (this.item && cleanName(this.item.name)) || '';
        this.mini.innerHTML = `<span class="mi">picture_in_picture_alt</span><span class="player-mini-title">${escapeHtml(title)}</span><span class="player-mini-cta">${t('player.backToPlayer')}</span>`;
        this.mini.classList.add('is-open');
        this.emit('minimize');
    }

    _restore(exitPip = false) {
        if (!this.minimized) return;
        this.minimized = false;
        this.root.classList.remove('is-minimized');
        if (this.isOpen) document.documentElement.classList.add('player-open');
        if (this.mini) this.mini.classList.remove('is-open');
        if (exitPip && document.pictureInPictureElement) document.exitPictureInPicture().catch(() => {});
        this._wake();
        this.emit('restore');
    }

    close() {
        if (!this.isOpen) return;
        ads.stop();
        if (this.minimized) this._restore(true);
        this._stopWatchdog();
        clearInterval(this._statsTimer);
        this.statsOn = false;
        this.el.stats.hidden = true;
        this._saveProgress(true);
        this._stopProgressTimer();
        clearInterval(this._recTimer);
        this._recTimer = null;
        this._recActive = null;
        this._cancelNextCountdown();
        this.core.unload();
        this.preview.setSource('');
        this._releaseLocal();
        this._clearSubs();
        this.isOpen = false;
        this.root.classList.remove('is-open');
        document.documentElement.classList.remove('player-open');
        this._closeSide();
        this._releaseWakeLock();
        if (document.pictureInPictureElement) document.exitPictureInPicture().catch(() => {});
        if (document.fullscreenElement && !device.isTV) document.exitFullscreen().catch(() => {});
        if (this._ownResolver && tvnav.layerResolver === this._ownResolver) tvnav.layerResolver = null;
        this._ownResolver = null;
        tvnav.onKey = null;
        tvnav.onEnter = null;
        clearTimeout(this.idleTimer);
        this.emit('close');
    }

    /** Botón «atrás» del mando o del aparato: si hay un panel o la tira de canales abiertos, los cierra; si no, sale del reproductor. */
    handleBack() {
        if (this.side) { this._closeSide(); return; }
        this.close();
    }

    /* ---------------- Acciones ---------------- */

    _action(name, btn) {
        switch (name) {
            case 'close':
                if (this.side) return this._closeSide();
                return this.close();
            case 'toggle': this.core.toggle(); this.el.bigplay.hidden = true; break;
            case 'rew': this.core.seekBy(-10); this._flash('replay_10'); break;
            case 'ffw': this.core.seekBy(10); this._flash('forward_10'); break;
            case 'prev': this.mode === 'live' ? this.zap(-1) : this._episodeStep(-1); break;
            case 'next': this.mode === 'live' ? this.zap(1) : this._episodeStep(1); break;
            case 'nextep': this._playNextEpisode(); break;
            case 'golive': this.catchup ? this.playLive() : this.core.goLive(); break;
            case 'mute': this.core.muted = !this.core.muted; break;
            case 'speed': this._cycleSpeed(); break;
            case 'fullscreen': this._toggleFullscreen(); break;
            case 'pip': this._togglePip(); break;
            case 'info': this._toggleInfo(); break;
            case 'fav': if (this.mode === 'live' && this.item) window.IPTV.actions.toggleFavorite('live', this.item.id); break;
            case 'report': this._report(); break;
            case 'record': this._recordSheet(); break;
            case 'channels': this._openSide('channels'); break;
            case 'episodes': this._openSide('episodes'); break;
            case 'side-episode': this._playEpisodeById(btn.getAttribute('data-id')); break;
            case 'side-season': this._renderEpisodes(Number(btn.getAttribute('data-id'))); break;
            case 'settings': this._openSide('settings'); break;
            case 'tracks': this._openSide('tracks'); break;
            case 'closeside': this._closeSide(); break;
            case 'retry': this._routeKey = ''; this.errorShown = false; this._loadCurrent(this.mode === 'live' ? 0 : this.core.currentTime); break;
            case 'cancelnext': this._countdownDismissed = true; this._cancelNextCountdown(); break;
            case 'dismissnext': this._nextOfferDismissed = true; this.el.next.classList.remove('is-open'); break;
            case 'playnext': this._playNextEpisode(); break;
            case 'side-channel': this._playChannelById(btn.getAttribute('data-id')); break;
            case 'side-cat': this._renderChannelSide(btn.getAttribute('data-id')); break;
            case 'side-catchup': this._playCatchup(Number(btn.getAttribute('data-start'))); break;
            case 'start-over': this._startOver(Number(btn.getAttribute('data-start'))); break;
            case 'side-record': this._recordProgram(Number(btn.getAttribute('data-start'))); break;
            case 'set-audio': {
                const index = Number(btn.getAttribute('data-index'));
                this._tracksTouched = true;
                this.core.setAudioTrack(index);
                const tr = this.core.audioTracks().find((x) => x.index === index);
                if (tr) this._rememberTracks({ audio: trackLang(tr) });
                this._renderTracksSide();
                break;
            }
            case 'set-sub': {
                const index = Number(btn.getAttribute('data-index'));
                this._tracksTouched = true;
                this._clearSubs({ forget: true });
                this.core.setSubtitleTrack(index);
                const tr = index >= 0 ? this.core.subtitleTracks().find((x) => x.index === index) : null;
                this._rememberTracks({ subs: tr ? trackLang(tr) : 'off' });
                this._renderTracksSide();
                break;
            }
            case 'intro-start': this._markIntro('start'); break;
            case 'intro-end': this._markIntro('end'); break;
            case 'intro-clear': { const k = this._seriesKey(); if (k) clearIntro(storage, k); this._introDraft = null; this._loadSegments(); notify.toast(t('intro.cleared'), { icon: 'check', duration: 1800 }); this._renderTracksSide(); break; }
            case 'subs-search': this._openSide('subs'); break;
            case 'subs-lang': settings.set('subtitleLang', btn.getAttribute('data-value')); this._renderSubsSide(); break;
            case 'subs-pick': this._pickSubs(btn.getAttribute('data-id')); break;
            case 'subs-shift': this._shiftSubs(Number(btn.getAttribute('data-value'))); break;
            case 'subs-back': this._openSide('tracks'); break;
            case 'set-level': this.core.setLevel(Number(btn.getAttribute('data-index'))); this._renderSettingsSide(); break;
            case 'set-variant': this._playVariant(btn.getAttribute('data-id'), { manual: true }); break;
            case 'variant-auto': this._variantManual = false; this._variantPicked = false; this._loadCurrent(0); this._renderSettingsSide(); break;
            case 'set-speed': this.core.playbackRate = Number(btn.getAttribute('data-value')); this._renderSettingsSide(); break;
            case 'set-fit': this._setFit(btn.getAttribute('data-value')); this._renderSettingsSide(); break;
            case 'toggle-stats': this._toggleStats(); this._renderSettingsSide(); break;
            case 'toggle-transcode':
                this.forceTranscode = !this.core.isTranscoding;
                this._closeSide();
                this._loadCurrent(this.core.currentTime);
                break;
            case 'side-guide': this._openSide('guide'); break;
            default: break;
        }
    }

    /** Tecla del mando normalizada: los WebView de Android TV no siempre dan `key` (llega «Unidentified») y
        canal +/− suele llegar como PageUp/PageDown; los números pueden venir solo con keyCode (48-57 o teclado numérico 96-105). */
    static remoteKey(e) {
        const key = e.key;
        const code = e.keyCode || e.which || 0;
        if (key === 'ChannelUp' || key === 'PageUp' || code === 33 || code === 166 || code === 427) return 'channelUp';
        if (key === 'ChannelDown' || key === 'PageDown' || code === 34 || code === 167 || code === 428) return 'channelDown';
        if (/^[0-9]$/.test(key || '')) return key;
        if (code >= 48 && code <= 57) return String(code - 48);
        if (code >= 96 && code <= 105) return String(code - 96);
        if (/^(Digit|Numpad)[0-9]$/.test(e.code || '')) return e.code.slice(-1);
        // Códigos del mando Samsung (Tizen): 415 play, 19 pausa, 10252 play/pausa, 413 stop, 417 avance, 412 retroceso, 427/428 canal.
        if (key === 'MediaPlayPause' || key === 'MediaPlay' || key === 'MediaPause' || code === 179 || code === 415 || code === 19 || code === 10252) return 'playPause';
        if (key === 'MediaStop' || code === 178 || code === 413) return 'stop';
        if (key === 'MediaTrackNext' || code === 176) return 'next';
        if (key === 'MediaTrackPrevious' || code === 177) return 'prev';
        if (key === 'MediaFastForward' || code === 228 || code === 417) return 'ff';
        if (key === 'MediaRewind' || code === 227 || code === 412) return 'rw';
        return '';
    }

    /**
     * OK en el mando con los controles ocultos (se está viendo el directo a pantalla completa): se abre la lista de canales y el
     * canal SIGUE reproduciéndose. Antes pulsaba el botón de pausa escondido. Para pausar, se usa su botón (o la tecla de pausa).
     * Solo en la tele o con el modo tele, y se puede cambiar en Ajustes → Reproducción («Al pulsar OK en el directo»).
     */
    _onEnter(e, target) {
        if (!this.isOpen || this.minimized || this._adActive || this.mode !== 'live' || this.side) return false;
        if (settings.get('liveOkAction') === 'pause') return false;
        if (!(device.isTV || document.documentElement.classList.contains('mode-tv'))) return false;
        if (!this.root.classList.contains('is-idle')) return false; // con los controles a la vista, OK pulsa el botón enfocado
        void e; void target;
        this._openSide('channels');
        return true;
    }

    _onNavKey(e, action) {
        if (!this.isOpen || this.minimized) return false;
        const key = e.key;
        const rk = PlayerUI.remoteKey(e);
        // Durante un anuncio no se pausa ni se salta nada; solo se puede salir.
        if (this._adActive && key !== 'Escape' && key !== 'Backspace' && rk !== 'stop') return true;
        // Teclas multimedia y de canal del mando
        if (rk === 'playPause') { this.core.toggle(); this._wake(); return true; }
        if (rk === 'stop') { this.close(); return true; }
        if (rk === 'channelUp' || rk === 'next') { this.mode === 'live' ? this.zap(1) : this._episodeStep(1); return true; }
        if (rk === 'channelDown' || rk === 'prev') { this.mode === 'live' ? this.zap(-1) : this._episodeStep(-1); return true; }
        if (rk === 'ff') { this.core.seekBy(30); this._flash('fast_forward'); this._wake(); return true; }
        if (rk === 'rw') { this.core.seekBy(-30); this._flash('fast_rewind'); this._wake(); return true; }
        if (/^[0-9]$/.test(rk) && this.mode === 'live' && !this.side) { this._digit(rk); return true; }
        if (key === 'i' || key === 'Info') { this._toggleInfo(); return true; }
        if (key === 'f') { this._toggleFullscreen(); return true; }
        if (key === 'm') { this.core.muted = !this.core.muted; return true; }
        if (key === 'k') { this.core.toggle(); this._wake(); return true; }
        if (key === 'j') { this.core.seekBy(-10); this._flash('replay_10'); return true; }
        if (key === 'l') { this.core.seekBy(10); this._flash('forward_10'); return true; }
        if (key === 'c') { this._openSide('channels'); return true; }

        if (!action) return false;
        const idle = this.root.classList.contains('is-idle');
        if (idle && !this.side) {
            // Con los controles ocultos, el mando actúa directamente sobre la reproducción.
            if (this.mode === 'live' && (action === 'up' || action === 'down')) { this.zap(action === 'up' ? 1 : -1); return true; }
            if (action === 'left' || action === 'right') {
                if (this.mode !== 'live' || this.core.dvrWindow() > 60) {
                    this.core.seekBy(action === 'left' ? -10 : 10);
                    this._flash(action === 'left' ? 'replay_10' : 'forward_10');
                    this._wake();
                    return true;
                }
            }
            this._wake();
            if (device.isTV) tvnav.focusLayer(this.root, this.el.playbtn);
            return true;
        }
        this._wake();
        return false;
    }

    zap(delta) {
        if (this.mode !== 'live' || !this.channelList.length) return;
        let idx = this.channelIndex;
        idx = (idx + delta + this.channelList.length) % this.channelList.length;
        this.channelIndex = idx;
        this.item = this.channelList[idx];
        this.catchup = null;
        this._flash('live_tv', cleanName(this.item.name));
        this._loadCurrent(0);
        this.emit('change', this.item);
    }

    playLive() {
        this.catchup = null;
        this._loadCurrent(0);
    }

    _playChannelById(id) {
        const idx = this.channelList.findIndex((c) => c.id === id);
        const channel = idx >= 0 ? this.channelList[idx] : catalog.get('live', id);
        if (!channel) return;
        this.channelIndex = idx;
        this.item = channel;
        this.catchup = null;
        this._resetVariants();
        this._closeSide();
        this._loadCurrent(0);
        this.emit('change', this.item);
    }

    _playCatchup(start) {
        const program = epg.programs(this.item.epgId).map((row) => ({ start: row[0], stop: row[1], title: row[2], desc: row[3] })).find((p) => p.start === start);
        if (!program || !catalog.canCatchup(this.item, program)) return;
        this.catchup = program;
        this._closeSide();
        this._loadCurrent(0);
    }

    _digit(d) {
        this.digits = (this.digits + d).slice(-4);
        this.el.chnum.textContent = this.digits;
        this.el.chnum.classList.add('is-open');
        clearTimeout(this.digitTimer);
        this.digitTimer = setTimeout(() => {
            const num = Number(this.digits);
            this.digits = '';
            this.el.chnum.classList.remove('is-open');
            const idx = this.channelList.findIndex((c) => c.num === num);
            if (idx >= 0) {
                this.channelIndex = idx;
                this.item = this.channelList[idx];
                this.catchup = null;
                this._loadCurrent(0);
                this.emit('change', this.item);
            } else {
                this._flash('error_outline', t('live.channelNumber', { num }) + ' ✕');
            }
        }, 1500);
    }

    async _episodeStep(delta) {
        if (this.mode !== 'episode' || !this.series) return;
        const info = await catalog.seriesInfo(this.series.id);
        const all = [];
        (info.seasons || []).forEach((s) => s.episodes.forEach((e) => all.push(e)));
        const idx = all.findIndex((e) => e.id === this.episode.id);
        const target = all[idx + delta];
        if (!target) return;
        this._saveProgress(true);
        this.episode = target;
        this._cancelNextCountdown();
        this._loadCurrent(0);
        this.emit('change', this.episode);
    }

    async _playNextEpisode() {
        if (this.mode !== 'episode' || !this.series || this._advancing) return;
        // Se para la cuenta atrás ya: si no, el reloj sigue y vuelve a pedir el siguiente mientras se carga este.
        this._advancing = true;
        this._cancelNextCountdown();
        try {
            const info = await catalog.seriesInfo(this.series.id);
            const next = catalog.nextEpisode(info, this.episode);
            if (!next) return this.close();
            this._saveProgress(true);
            this.episode = next;
            this._loadCurrent(0);
            this.emit('change', this.episode);
        } finally {
            this._advancing = false;
        }
    }

    _cycleSpeed() {
        const i = SPEEDS.indexOf(this.core.playbackRate);
        this.core.playbackRate = SPEEDS[(i + 1) % SPEEDS.length];
    }

    _setFit(value) {
        this.fit = value;
        this.root.classList.toggle('is-fill', value === 'cover');
    }

    _toggleFullscreen() {
        const doc = document;
        const el = this.root;
        if (doc.fullscreenElement || doc.webkitFullscreenElement) {
            (doc.exitFullscreen || doc.webkitExitFullscreen).call(doc);
        } else if (el.requestFullscreen) {
            el.requestFullscreen().catch(() => {});
        } else if (el.webkitRequestFullscreen) {
            el.webkitRequestFullscreen();
        } else if (this.video.webkitEnterFullscreen) {
            this.video.webkitEnterFullscreen(); // iPhone: solo el vídeo puede ir a pantalla completa
        }
    }

    async _togglePip() {
        try {
            if (document.pictureInPictureElement) await document.exitPictureInPicture();
            else if (document.pictureInPictureEnabled) await this.video.requestPictureInPicture();
        } catch (e) {
            notify.toast(t('player.pip') + ' ✕');
        }
    }

    /** Grabar el canal en directo: hasta el final del programa (EPG) o un tiempo fijo. */
    _recordSheet() {
        const app = window.app;
        const ch = this.item;
        const account = window.IPTV.account;
        if (!account.canRecord || !ch || this.mode !== 'live') return;
        // Si este canal ya se está grabando, el botón sirve para pararlo (y guardar lo grabado).
        const active = this._recActive && this._recActive.get(String(ch.id));
        if (active) {
            const stopButtons = [
                { text: t('rec.stopHere'), bold: true, onClick: async () => {
                    try {
                        await account.stopRecording(active.id);
                        notify.toast(t('rec.stopped', { title: active.title }), { icon: 'stop_circle', duration: 3500 });
                        active.stopping = true;
                        this._renderRecordButton(true);
                    } catch (e) {
                        notify.error(t('common.errorNetwork'));
                    }
                } },
                { text: t('rec.manage'), onClick: () => { this.close(); window.IPTV.app.views.main.router.navigate('/recordings/'); } },
                { text: t('common.cancel'), color: 'red' }
            ];
            app.actions.create({ buttons: [[{ text: t('rec.recordingNow', { title: active.title }), label: true }], stopButtons] }).open();
            return;
        }
        const nn = epg.nowNext(ch.epgId);
        const now = Date.now();
        const maxMs = (account.recordings.maxMinutes || 240) * 60000;
        const buttons = [];
        const go = (title, endAt) => async () => {
            if (!(await this._recordConnOk(ch))) return;
            try {
                await account.scheduleRecording({ channelId: ch.id, channelName: cleanName(ch.name), url: catalog.rawLiveUrl(ch), title, startAt: now, endAt: Math.min(endAt, now + maxMs) });
                notify.toast(t('rec.started', { title }), { icon: 'fiber_manual_record' });
                this._renderRecordButton(true);
            } catch (e) {
                notify.error(t('rec.error.' + (e.code || 'generic'), {}) || t('common.errorNetwork'));
            }
        };
        if (nn.now && nn.now.stop > now + 60000) {
            buttons.push({ text: t('rec.untilEnd', { title: nn.now.title, time: formatClock(nn.now.stop) }), bold: true, onClick: go(nn.now.title, nn.now.stop + (account.recordings.endMarginSec || 0) * 1000) });
        }
        [30, 60, 120].forEach((min) => buttons.push({ text: t('rec.minutes', { n: min }), onClick: go((nn.now ? nn.now.title : cleanName(ch.name)), now + min * 60000) }));
        buttons.push({ text: t('rec.manage'), onClick: () => { this.close(); window.IPTV.app.views.main.router.navigate('/recordings/'); } });
        buttons.push({ text: t('common.cancel'), color: 'red' });
        app.actions.create({ buttons: [buttons] }).open();
    }

    _renderRecordButton(refresh) {
        if (!this.el.recbtn) return;
        const account = window.IPTV.account;
        const can = this.mode === 'live' && !this.catchup && account && account.canRecord;
        this.el.recbtn.hidden = !can;
        const active = can && this._recActive && this.item ? this._recActive.get(String(this.item.id)) : null;
        this.el.recbtn.classList.toggle('is-recording', !!active);
        this.el.recbtn.setAttribute('aria-label', active ? t('rec.stop') : t('rec.record'));
        this.el.recbtn.querySelector('.mi').textContent = active ? 'stop_circle' : 'fiber_manual_record';
        // Qué canales se están grabando: al abrir un canal y cada 30 s mientras el reproductor sigue abierto.
        if (can && (refresh || !this._recCheckedAt || Date.now() - this._recCheckedAt > 20000)) this._refreshRecStatus();
        if (can && !this._recTimer) this._recTimer = setInterval(() => this._refreshRecStatus(), 30000);
    }

    /** Pregunta al servidor qué grabaciones hay en curso y repinta el botón. */
    _refreshRecStatus() {
        this._recCheckedAt = Date.now();
        const account = window.IPTV.account;
        if (!account || !account.canRecord) return;
        account.listRecordings().then((r) => {
            const map = new Map();
            (r.recordings || []).forEach((j) => { if (j.status === 'recording') map.set(String(j.channelId), j); });
            this._recActive = map;
            if (this.isOpen && this.el.recbtn) {
                const active = this.item ? map.get(String(this.item.id)) : null;
                this.el.recbtn.classList.toggle('is-recording', !!active);
                this.el.recbtn.setAttribute('aria-label', active ? t('rec.stop') : t('rec.record'));
                this.el.recbtn.querySelector('.mi').textContent = active ? 'stop_circle' : 'fiber_manual_record';
            }
        }).catch(() => {});
    }

    /** «Denunciar»: abre el formulario ya rellenado con lo que se está viendo (canal de una lista pública, película de Cine clásico o título). */
    _report() {
        const item = this.mode === 'episode' ? this.series : this.item;
        if (!item) return;
        const name = this.mode === 'episode' && this.episode ? `${item.name} · T${this.episode.season} E${this.episode.num}` : item.name;
        const pid = this.route && this.route.pid;
        const prof = pid ? (pid === catalog.id ? catalog.profile : (catalog.sources.get(pid) || {}).profile) : null;
        const preset = prof ? presetOf(prof) : null;
        let opts;
        if (this.mode === 'live' && prof && prof.type === 'm3u' && preset) opts = { target: { type: 'channel', id: preset.id, title: name, text: item.name } };
        else if (this.mode === 'vod' && /(^|~)ia-/.test(String(item.id))) opts = { target: { type: 'archive', id: String(item.id).replace(/^.*~/, ''), title: name } };
        else opts = { prefill: (this.mode === 'live' ? t('report.prefillChannel', { name }) : name) };
        if (this.mode !== 'live') this.core.pause();
        openReport(opts);
    }

    _syncFav() {
        const b = this.el && this.el.favbtn;
        if (!b || this.mode !== 'live' || !this.item) return;
        const on = catalog.isFavorite('live', this.item.id);
        b.classList.toggle('is-active', on);
        b.querySelector('.mi').textContent = on ? 'favorite' : 'favorite_border';
        b.setAttribute('aria-label', on ? t('common.favoriteRemove') : t('common.favoriteAdd'));
        b.setAttribute('title', on ? t('common.favoriteRemove') : t('common.favoriteAdd'));
    }

    _toggleInfo() {
        const open = !this.el.info.classList.contains('is-open');
        this.el.info.classList.toggle('is-open', open);
        if (open) this._renderInfo();
        this._wake();
    }

    /* ---------------- Recordar audio y subtítulos por serie ---------------- */

    _seriesKey() {
        return this.mode === 'episode' && this.series ? String(this.series.id) : '';
    }

    _rememberTracks(patch) {
        const key = this._seriesKey();
        if (key) rememberTracks(storage, key, patch);
    }

    /** Al empezar un episodio: el audio y los subtítulos que se eligieron en esta serie. Las pistas aparecen con retraso, se reintenta. */
    _scheduleTrackPrefs() {
        const key = this._seriesKey();
        this._tracksTouched = false;
        const seq = (this._trackSeq = (this._trackSeq || 0) + 1);
        if (!key || !getTrackPrefs(storage, key)) return;
        [500, 1500, 3500, 7000].forEach((ms) => setTimeout(() => {
            if (seq !== this._trackSeq || !this.isOpen || this._tracksTouched) return;
            this._applyTrackPrefs(key);
        }, ms));
    }

    _applyTrackPrefs(key) {
        const pref = getTrackPrefs(storage, key);
        if (!pref) return;
        try {
            if (pref.audio) {
                const audio = this.core.audioTracks();
                const want = findTrack(audio, pref.audio);
                if (want && !want.active) this.core.setAudioTrack(want.index);
            }
            if (pref.subs === 'off') {
                if (!this.subs && this.core.subtitleTracks().some((x) => x.active)) this.core.setSubtitleTrack(-1);
            } else if (pref.subs && !this.subs) {
                const want = findTrack(this.core.subtitleTracks(), pref.subs);
                if (want && !want.active) this.core.setSubtitleTrack(want.index);
            }
        } catch (e) { /* una pista que no se deja elegir no debe romper la reproducción */ }
    }

    /* ---------------- Marcar la intro a mano (para series sin marcas del servidor) ---------------- */

    _markIntro(which) {
        const key = this._seriesKey();
        if (!key) return;
        const pos = this.core.currentTime || 0;
        const clock = formatDuration(pos);
        if (which === 'start') {
            this._introDraft = { start: pos };
            notify.toast(t('intro.startMarked', { time: clock }), { icon: 'flag', duration: 3500 });
        } else {
            const start = this._introDraft ? this._introDraft.start : null;
            if (start == null) { notify.toast(t('intro.needStart'), { icon: 'info', duration: 3500 }); return; }
            if (!setIntro(storage, key, start, pos)) { notify.error(t('intro.tooShort')); return; }
            this._introDraft = null;
            notify.toast(t('intro.saved'), { icon: 'check_circle', duration: 3500 });
            this._loadSegments();
        }
        this._renderTracksSide();
    }

    /* ---------------- Saltar intro y créditos (marcas de Jellyfin, Emby y Plex, o la que se marque a mano) ---------------- */

    /** Pide las marcas del título que se va a ver (si su servidor las tiene). No retrasa la reproducción. */
    _loadSegments() {
        this._segments = [];
        this._renderSkip();
        if (this.mode === 'live') return;
        const item = this.mode === 'vod' ? this.item : this.episode;
        const seq = ++this._segSeq;
        // La intro marcada a mano en esta serie vale cuando el servidor no da ninguna (o no hay servidor de marcas).
        const manual = () => {
            const key = this._seriesKey();
            const m = key ? getIntro(storage, key) : null;
            return m ? [{ type: 'intro', start: m.start, end: m.end }] : [];
        };
        this._segments = manual();
        this._renderSkip();
        catalog.segmentsFor(item, Number(item && item.durationSecs) || 0).then((list) => {
            if (seq !== this._segSeq || !this.isOpen) return;
            const server = list || [];
            this._segments = server.some((s) => s.type === 'intro') ? server : server.concat(manual()).sort((a, b) => a.start - b.start);
        }).catch(() => {});
    }

    /** Enseña «Saltar intro» o «Saltar créditos» mientras se está dentro de una de esas marcas. */
    _renderSkip() {
        const btn = this.el && this.el.skip;
        if (!btn) return;
        const pos = this.core.currentTime;
        const seg = this._segments && this._segments.length ? this._segments.find((s) => pos >= s.start - 0.5 && pos < s.end - 1.5) : null;
        if (!seg || this._adActive || (seg.type === 'credits' && this.el.next.classList.contains('is-open'))) {
            if (!btn.hidden) btn.hidden = true;
            return;
        }
        this._skipSeg = seg;
        const label = t(seg.type === 'intro' ? 'player.skipIntro' : 'player.skipCredits');
        if (btn.hidden || btn.textContent !== label) btn.innerHTML = `<span class="mi">skip_next</span>${label}`;
        btn.hidden = false;
    }

    _doSkip() {
        const seg = this._skipSeg;
        if (!seg) return;
        const dur = this.core.duration || 0;
        // Créditos que llegan hasta el final de un episodio: se pasa al siguiente (si lo hay).
        if (seg.type === 'credits' && this.mode === 'episode' && dur && seg.end >= dur - 2) {
            this._episodeStep(1);
            return;
        }
        this.core.seek(seg.end);
        this.el.skip.hidden = true;
    }

    /* ---------------- Anuncios (prototipo; ver core/ads.js) ---------------- */

    /** ¿Este título lleva anuncios? No los grabados por el usuario en el servidor ni lo descargado. */
    _adsApply() {
        if (!ads.enabledFor(this.mode, this._adFree)) return false;
        if (this.mode === 'vod' && this.item && /^rec:/.test(String(this.item.id))) return false;
        return true;
    }

    /** Reproduce un anuncio sobre el vídeo y vuelve a lo que había. Devuelve false si el reproductor se cerró entretanto. */
    async _playAd() {
        const seq = this._adSeq = (this._adSeq || 0) + 1;
        this._adActive = true;
        this.root.classList.add('is-ad');
        this._spinner(false);
        try {
            await ads.play({ container: this.el.adlayer, video: this.video });
        } finally {
            this._adActive = false;
            this.root.classList.remove('is-ad');
        }
        return this.isOpen && seq === this._adSeq;
    }

    /** Anuncio al empezar: una vez por título (no se repite si se prueba otro proveedor) y respetando el intervalo mínimo. */
    async _preroll(startPosition, route) {
        // Contenido de derechos libres (el cine clásico de Internet Archive): es donde se pueden poner anuncios sin riesgo.
        const profile = route && route.target ? catalog.profileOf(route.target) : null;
        this._adFree = !!(profile && profile.type === 'archive');
        const key = this.mode + ':' + (this.mode === 'episode' ? (this.episode && this.episode.id) : (this.item && this.item.id));
        if (!this._adsApply() || this._adKey === key) return true;
        this._adKey = key;
        const c = ads.cfg;
        if (this.mode !== 'live') this._midAt = (startPosition || 0) + (c.midrollSec || 0);
        if (!c.preroll || !ads.gapOk()) return true;
        const open = await this._playAd();
        if (open) this._spinner(true);
        return open;
    }

    /** Anuncio en medio: cada `midrollSec` segundos viendo películas y episodios, nunca casi al final (últimos dos minutos, o el 15 % si dura poco). */
    async _maybeMidroll() {
        const c = ads.cfg;
        if (!c || !c.midrollSec || this.mode === 'live' || this._adActive || this._midAt == null || !this._adsApply()) return;
        const t = this.core.currentTime;
        const d = this.core.duration;
        if (!(t >= this._midAt) || this.core.paused || (d && d - t < Math.min(120, d * 0.15))) return;
        this._midAt = t + c.midrollSec;
        if (!ads.gapOk()) return;
        this.core.pause();
        const open = await this._playAd();
        if (open) this.core.play();
    }

    /* ---------------- Progreso ---------------- */

    _startProgressTimer() {
        this.progressTimer = setInterval(() => this._saveProgress(false), PROGRESS_SAVE_MS);
    }

    _stopProgressTimer() {
        clearInterval(this.progressTimer);
        this.progressTimer = null;
    }

    _saveProgress(force) {
        if (!this.isOpen || this.mode === 'live') return;
        const duration = this.core.duration;
        const position = this.core.currentTime;
        if (!duration || (!force && this.core.paused)) return;
        // Copia síncrona en localStorage: si la página se descarga (F5) antes de que IndexedDB
        // termine de escribir, el enlace /watch/… retoma igualmente desde aquí.
        storage.set('lastWatch', { mode: this.mode, id: this.mode === 'vod' ? this.item.id : this.episode ? this.episode.id : null, position, duration, updated: Date.now() });
        if (this.mode === 'vod') {
            catalog.setProgress('vod', this.item.id, { position, duration, name: this.item.name, poster: this.item.poster });
            catalog.reportPlayback('vod', this.item, { position, duration }, { stopped: !!force });
            catalog.countPlay('vod', this.item, position);
        } else if (this.episode) {
            catalog.reportPlayback('episode', this.episode, { position, duration }, { stopped: !!force });
            catalog.countPlay('series', this.series, position);
            catalog.setProgress('episode', this.episode.id, {
                position, duration,
                seriesId: this.series.id, season: this.episode.season, episode: this.episode.num,
                title: this.episode.title, poster: this.series.poster
            });
        }
    }

    _onEnded() {
        this._saveProgress(true);
        if (this.mode === 'episode' && settings.get('autoplayNext')) {
            this._startNextCountdown();
        } else if (this.mode === 'live' && this.catchup) {
            this.playLive();
        } else if (this.mode === 'vod') {
            this._wake(true);
        }
    }

    /** Deja a mano el siguiente episodio (para el botón de «Siguiente episodio» que sale hacia el final). */
    _prepareNext() {
        this._nextEp = null;
        this._nextOffered = false;
        this._nextOfferDismissed = false;
        this._countdownDismissed = false;
        this._cancelNextCountdown();
        if (this.mode !== 'episode' || !this.series) return;
        const ep = this.episode;
        catalog.seriesInfo(this.series.id).then((info) => { if (this.episode === ep) this._nextEp = catalog.nextEpisode(info, ep); }).catch(() => {});
    }

    /**
     * Como Netflix: unos minutos antes del final (3 por defecto, configurable en Ajustes) sale el botón
     * «Siguiente episodio» junto a «Ver créditos». No cambia nada solo: es una oferta. Al terminar el episodio, si el
     * ajuste lo permite, el propio botón se va llenando y pasa al siguiente (ver _startNextCountdown).
     */
    _maybeOfferNext() {
        if (this.mode !== 'episode' || !this._nextEp || this._nextOffered || this._nextOfferDismissed || this._adActive || this.nextCountdown || this.errorShown) return;
        const d = this.core.duration;
        const pos = this.core.currentTime;
        // Antelación elegida en Ajustes (por defecto 3 minutos; 0 = solo al terminar), sin pasar de un cuarto del episodio.
        const setting = Number(settings.get('nextOfferSec'));
        const lead = Math.min(setting >= 0 && isFinite(setting) ? setting : 180, d * 0.25);
        if (!d || d < 240 || !pos || !(lead > 0)) return;
        if (d - pos > lead) return;
        this._showNext({ ending: false });
        this._nextOffered = true;
    }

    /** Pinta el panel de «Siguiente episodio». `ending`: el episodio acabó y el botón se llena hasta pasar solo. */
    _showNext({ ending }) {
        const next = this._nextEp;
        if (!next) return;
        const label = `${t('series.nextEpisode')}`;
        this.el.next.innerHTML = `
            <button type="button" class="player-next-ghost" data-action="${ending ? 'cancelnext' : 'dismissnext'}">${escapeHtml(ending ? t('series.autoplayCancel') : t('player.viewCredits'))}</button>
            <button type="button" class="player-next-go" data-action="playnext" data-nav-default aria-label="${escapeHtml(label + ': T' + next.season + ' E' + next.num + ' · ' + next.title)}">
                <i class="player-next-fill"></i><span class="mi">play_arrow</span><span class="player-next-label">${escapeHtml(label)}</span>
            </button>`;
        this.el.next.classList.add('is-open');
        this._wake(true);
        if (device.isTV) tvnav.focusLayer(this.root, this.el.next.querySelector('[data-action="playnext"]'));
        if (this.el.skip) this.el.skip.hidden = true;
    }

    async _startNextCountdown() {
        // «Terminado» puede llegar más de una vez (el reproductor nativo lo repetía): una sola cuenta atrás.
        if (this.nextCountdown || this._countdownStarting || this._countdownDismissed) return;
        this._countdownStarting = true;
        try {
            if (!this._nextEp) {
                const info = await catalog.seriesInfo(this.series.id);
                this._nextEp = catalog.nextEpisode(info, this.episode);
            }
        } finally {
            this._countdownStarting = false;
        }
        if (!this._nextEp || !this.isOpen || this.nextCountdown) return;
        this._showNext({ ending: true });
        // El botón es la barra de progreso: se llena en 10 s y entonces pasa al siguiente.
        const fill = this.el.next.querySelector('.player-next-fill');
        void fill.offsetWidth;
        fill.style.transition = 'width 10s linear';
        fill.style.width = '100%';
        this.nextCountdown = setTimeout(() => this._playNextEpisode(), 10000);
    }

    _cancelNextCountdown() {
        clearTimeout(this.nextCountdown);
        clearInterval(this.nextCountdown);
        this.nextCountdown = null;
        this.el.next.classList.remove('is-open');
    }

    /* ---------------- Errores ---------------- */

    /* Vigilante de arranque. Avisa con diagnóstico solo cuando no llega nada: pasados 25 s
       (60 s con audio convertido) sin reproducir y, además, sin recibir datos en los últimos 20 s.
       Si los datos llegan despacio pero llegan (proveedor lento), se sigue esperando con el
       spinner en lugar de dar por perdida la reproducción; el aviso desaparece si al final arranca. */
    _startWatchdog(transcoding = false) {
        this._stopWatchdog();
        const startedAt = Date.now();
        const minWait = transcoding ? 60000 : 25000;
        const stallMs = 20000;
        this._watchdogProgress = 0;
        this._watchdog = setInterval(() => {
            if (!this.isOpen || !this.el.error.hidden) return this._stopWatchdog();
            const v = this.core.video;
            if (v.currentTime > 0 && !v.paused) return this._stopWatchdog();
            if (!this.el.bigplay.hidden) return this._stopWatchdog();
            const now = Date.now();
            if (now - startedAt < minWait) return;
            const lastData = Math.max(this._watchdogProgress || 0, startedAt);
            if (now - lastData < stallMs) return; // siguen llegando datos: paciencia
            this._stopWatchdog();
            this._onError({ type: 'timeout', fatal: true });
        }, 2000);
    }

    _stopWatchdog() {
        clearInterval(this._watchdog);
        this._watchdog = null;
    }

    _onError(err) {
        // Mientras se cambia de proveedor, los errores que aún lleguen son del anterior.
        if (this._switching || this._retryPending) return;
        this._spinner(false);
        this._stopWatchdog();
        if (this.errorShown) return;
        // Respaldo: si el título está también en otro proveedor, se sigue desde allí.
        const next = err.final || err.type === 'transcode' ? null : this._nextRoute();
        if (next) {
            const from = this.route ? this.route.name : '';
            // Otra emisión del mismo proveedor, u otro proveedor.
            const same = this.route && this.route.pid === next.pid;
            notify.toast(same ? t('player.fallbackStream') : t(from ? 'player.fallback' : 'player.fallbackTo', { from, to: next.name }), { icon: 'swap_horiz', duration: 3500 });
            this._switching = true;
            const done = () => setTimeout(() => { this._switching = false; }, 1500);
            this._loadCurrent(this.mode === 'live' ? 0 : this.core.currentTime || this._startWanted || 0).then(done, done);
            return;
        }
        // La conversión falló nada más empezar (p. ej. el panel aún tenía abierta la conexión anterior y limitó la nueva): un
        // reintento a los 2,5 s suele bastar.
        if (this.core.isTranscoding && this.mode !== 'live' && (this._transcodeRetries || 0) < 1) {
            this._transcodeRetries = (this._transcodeRetries || 0) + 1;
            this._flash('sync', t('player.retrying'));
            this._retryPending = true;
            setTimeout(() => { this._retryPending = false; if (this.isOpen && !this.errorShown) this._loadCurrent(this.core.currentTime || this._startWanted || 0); }, 2500);
            return;
        }
        // Error de formato en bajo demanda (MKV con Dolby/DTS…): si hay conversión de audio (servidor o la local de la app
        // de Windows) y aún no se ha probado, se reintenta convirtiendo antes de rendirse.
        if (err.type === 'media' && this.mode !== 'live' && !this.core.isTranscoding && this.forceTranscode == null && !this._triedTranscode && transcodeAvailable()) {
            this._triedTranscode = true;
            this.forceTranscode = true;
            notify.toast(t('player.retryTranscode'), { icon: 'graphic_eq', duration: 3500 });
            this._loadCurrent(this.core.currentTime || this._startWanted || 0);
            return;
        }
        this.errorShown = true;
        if (err.type === 'expired' || err.type === 'disabled') {
            const name = err.name || (catalog.profile ? catalog.profile.name : '');
            const date = err.sub && err.sub.expiresAt ? formatDate(err.sub.expiresAt, i18n.lang) : '';
            this.el.errortitle.textContent = t(err.type === 'expired' ? 'player.expiredTitle' : 'player.disabledTitle');
            this.el.errortext.textContent = err.type === 'disabled' ? t('player.disabledText', { name })
                : t(date ? 'player.expiredText' : 'player.expiredTextNoDate', { name, date }) + (err.sub && err.sub.simulated ? ' ' + t('sub.simulated') : '');
            this.el.error.hidden = false;
            this._wake(true);
            if (device.isTV) tvnav.focusLayer(this.root, this.el.error.querySelector('[data-action="retry"]'));
            return;
        }
        this.el.errortitle.textContent = t('player.errorTitle');
        const msg = err.type === 'network' ? t('player.errorNetwork')
            : err.type === 'media' ? t('player.errorMedia')
                : err.type === 'timeout' ? t('player.errorTimeout')
                    : err.type === 'transcode' ? (err.status === 503 ? t('player.errorTranscodeBusy') : err.status === 401 ? t('player.errorTranscodeAuth') : t('player.errorTranscode'))
                        : t('player.errorGeneric');
        const d = this.core.diagnostics();
        const parts = [d.engine, d.video ? d.video + (d.size ? ' ' + d.size : '') : '', d.audio, err.details || '', d.mediaError ? 'media ' + d.mediaError : '', d.host || '', device.platformLabel()].filter(Boolean);
        this.el.errortext.innerHTML = escapeHtml(msg + (err.status ? ' (HTTP ' + err.status + ')' : '')) + `<br/><small class="player-error-diag">${escapeHtml(parts.join(' · '))}</small>`;
        // Conversión local (app de Windows): lo que dijo ffmpeg, para no tener que abrir el registro.
        if (this.core.isTranscoding && localTranscodeAvailable() && window.electronBridge) {
            localTranscodeLastError(window.electronBridge).then((why) => { if (why && !this.el.error.hidden) this.el.errortext.insertAdjacentHTML('beforeend', `<br/><small class="player-error-diag">ffmpeg: ${escapeHtml(why.slice(0, 300))}</small>`); });
        }
        this.el.error.hidden = false;
        this._wake(true);
        if (device.isTV) tvnav.focusLayer(this.root, this.el.error.querySelector('[data-action="retry"]'));
    }

    /* ---------------- Render ---------------- */

    _renderTitle() {
        let title = '';
        let sub = '';
        if (this.mode === 'live') {
            title = cleanName(this.item.name);
            const nn = epg.nowNext(this.item.epgId);
            if (this.catchup) sub = t('live.catchupOf', { title: this.catchup.title });
            else if (nn.now) sub = t('common.now') + ': ' + nn.now.title;
            else sub = catalog.category('live', this.item.categoryId) ? catalog.category('live', this.item.categoryId).name : '';
        } else if (this.mode === 'vod') {
            title = stripCountry(this.item.name);
            sub = [this.item.year, this.item.genre].filter(Boolean).join(' · ');
        } else {
            title = stripCountry(this.series.name);
            sub = 'T' + this.episode.season + ' E' + this.episode.num + ' · ' + this.episode.title;
        }
        this.el.title.textContent = title;
        this.el.subtitle.textContent = sub;
        this.el.livebtn.classList.toggle('is-live', this.mode === 'live' && !this.catchup);
        this.el.livetext.textContent = this.catchup ? t('live.goLive') : t('common.live');
    }

    /** De dónde llega lo que se está viendo: el proveedor en uso (cambia si salta a un respaldo) o el propio dispositivo. */
    _sourceText() {
        if (this.local) return t('player.fromDownload');
        return this.route && this.route.name ? this.route.name : '';
    }

    _sourceHtml() {
        const s = this._sourceText();
        return (s ? `<div class="player-info-source"><span class="mi">dns</span>${escapeHtml(t('detail.provider'))}: <b>${escapeHtml(s)}</b></div>` : '') + this._reportHtml();
    }

    /** «Denunciar»: ya no está en la barra del reproductor; se encuentra aquí, en la información. */
    _reportHtml() {
        return `<button type="button" class="player-info-report" data-action="report"><span class="mi">flag</span>${escapeHtml(t('player.report'))}</button>`;
    }

    /* ---------------- Calidad por variantes del mismo canal (SD/HD/FHD) ---------------- */

    _resetVariants() {
        this._variants = this.mode === 'live' ? catalog.channelVariants(this.item.id) : [];
        this._stalls = [];
        this._stallSince = 0;
        this._variantPicked = false;
        this._variantManual = false;
        this._lastVariantSwitch = 0;
        this._started = false;
    }

    /** Caudal estimado (kb/s): lo que mide el motor ahora, o la última medida guardada (10 min). */
    _netEstimateKbps() {
        const live = this.core.bandwidthKbps ? this.core.bandwidthKbps() : 0;
        if (live) { storage.set('netKbps', { kbps: live, at: Date.now() }); return live; }
        const saved = storage.get('netKbps');
        return saved && Date.now() - (saved.at || 0) < 10 * 60000 ? Number(saved.kbps) || 0 : 0;
    }

    /** Al abrir un canal: con variantes y según el ajuste, puede abrirse otra (p. ej. HD en vez de FHD si la conexión no da). */
    _applyStartVariant() {
        if (this._variantPicked || this._variantManual || !this._variants || this._variants.length < 2) return;
        this._variantPicked = true;
        const mode = settings.get('liveQuality') || 'auto';
        const estimate = this._netEstimateKbps();
        const pick = pickVariant({ variants: this._variants, current: this.item, estimateKbps: estimate, mode });
        if (!pick || pick.id === this.item.id) return;
        this._setVariantItem(pick);
        if (mode === 'auto') this._flash('network_check', t('player.variantStart', { mbps: (estimate / 1000).toFixed(1), to: variantLabel(pick) }));
    }

    _setVariantItem(ch) {
        this.item = ch;
        const idx = this.channelList.findIndex((c) => c.id === ch.id);
        if (idx >= 0) this.channelIndex = idx;
        this._lastVariantSwitch = Date.now();
        this.emit('change', this.item);
    }

    _playVariant(id, { manual = false } = {}) {
        const ch = (this._variants || []).find((c) => c.id === id);
        if (!ch) return;
        this._variantManual = manual;
        this._variantPicked = true;
        this._stalls = [];
        this._setVariantItem(ch);
        this._closeSide();
        this._loadCurrent(0);
    }

    _onStallStart() {
        if (!this._started || this.mode !== 'live' || this.catchup) return;
        if (Date.now() - (this._seekAt || 0) < 2000) return; // tras buscar es normal cargar
        this._stallSince = Date.now();
        this._stalls.push({ at: this._stallSince, long: false });
        clearTimeout(this._stallTimer);
        // Una parada larga cuenta doble aunque no se repita.
        this._stallTimer = setTimeout(() => { const s = this._stalls[this._stalls.length - 1]; if (s && this._stallSince) { s.long = true; this._maybeDowngrade(); } }, 8000);
        this._maybeDowngrade();
    }

    _onStallEnd() {
        this._started = true;
        this._stallSince = 0;
        clearTimeout(this._stallTimer);
    }

    /** Cortes repetidos en directo: se baja a la variante inmediatamente peor, salvo que el usuario haya elegido a mano o quiera «siempre la mejor». */
    _maybeDowngrade() {
        if (this.mode !== 'live' || this.catchup || this._variantManual || !this._variants || this._variants.length < 2) return;
        if ((settings.get('liveQuality') || 'auto') === 'best') return;
        if (!shouldDowngrade(this._stalls, { lastSwitchAt: this._lastVariantSwitch })) return;
        const lower = lowerVariant(this._variants, this.item);
        if (!lower) return;
        this._stalls = [];
        this._setVariantItem(lower);
        notify.toast(t('player.variantDown', { to: variantLabel(lower) }), { icon: 'network_check', duration: 4000 });
        this._loadCurrent(0);
    }

    _variantMenuHtml(levels) {
        if (this.mode !== 'live' || !this._variants || this._variants.length < 2 || (levels && levels.length > 1)) return '';
        const auto = !this._variantManual;
        return `<div class="player-menu-title">${t('player.quality')}</div>
            <button class="${auto ? 'is-active' : ''}" data-action="variant-auto" data-nav-default><span class="mi">check</span>${t('player.variantAuto')}</button>
            ${this._variants.map((ch) => `<button class="${this._variantManual && ch.id === this.item.id ? 'is-active' : ''}" data-action="set-variant" data-id="${escapeHtml(ch.id)}"><span class="mi">${ch.id === this.item.id ? 'check' : ''}</span>${escapeHtml(variantLabel(ch))}</button>`).join('')}`;
    }

    _netHtml() {
        if (this.mode !== 'live') return '';
        const kbps = this._netEstimateKbps();
        if (!kbps) return '';
        const need = needKbps(this.item.name);
        const ok = kbps >= need;
        return `<p class="small ${ok ? 'muted' : 'warn'}"><span class="mi" style="font-size:16px;vertical-align:-3px">${ok ? 'network_check' : 'signal_wifi_statusbar_connected_no_internet_4'}</span> ${t('player.netLine', { mbps: (kbps / 1000).toFixed(1) })} · ${t('player.needLine', { mbps: (need / 1000).toFixed(1) })}</p>`;
    }

    /** Directo «estilo guía»: el programa en emisión (con su barra), los siguientes, «Ver desde el inicio», «Últimos N días» y los atajos V.O. / SUB. */
    _liveGuideHtml() {
        const now = Date.now();
        const list = epg.schedule(this.item.epgId, { past: 0, future: 5 });
        const cur = list.find((p) => p.start <= now && p.stop > now) || null;
        const next = list.filter((p) => p.start > now).slice(0, cur ? 3 : 4);
        const ch = this.item;
        const logo = ch.logo ? `<img class="pg-logo" src="${escapeHtml(imageUrl(ch.logo))}" alt="" onerror="this.remove()"/>` : '';
        const canStartOver = !!cur && !this.catchup && (catalog.canCatchup(ch, cur) || this._canRewindTo(cur.start));
        const days = Number(ch.archive ? ch.archiveDays || 0 : 0);
        const audioN = this.core.audioTracks ? this.core.audioTracks().length : 0;
        const subN = this.core.subtitleTracks ? this.core.subtitleTracks().length : 0;
        const row = (p, kind) => {
            const time = kind === 'now' ? `<span class="pg-times">${formatClock(p.start)} <span class="pg-bar"><i style="width:${programProgress(p)}%"></i></span> ${formatClock(p.stop)}</span>`
                : `<span class="pg-times">${t('live.startsAt', { time: formatClock(p.start) })}</span>`;
            return `<div class="pg-row ${kind === 'now' ? 'is-now' : ''}">
                <div class="pg-main"><div class="pg-title">${escapeHtml(p.title)}</div>${kind === 'now' && splitProgramDesc(p.desc).plot ? `<div class="pg-desc">${escapeHtml(splitProgramDesc(p.desc).plot)}</div>` : ''}</div>
                ${time}
            </div>`;
        };
        const actions = [
            canStartOver ? `<button type="button" class="pg-btn" data-action="start-over" data-start="${cur.start}" data-nav-default><span class="mi">replay</span>${t('live.watchFromStart')}</button>` : '',
            audioN > 1 ? `<button type="button" class="pg-btn pg-btn-chip" data-action="tracks" title="${escapeHtml(t('player.audio'))}">V.O.</button>` : '',
            subN > 0 ? `<button type="button" class="pg-btn pg-btn-chip" data-action="tracks" title="${escapeHtml(t('player.subtitles'))}">SUB</button>` : '',
            days > 1 ? `<button type="button" class="pg-btn" data-action="side-guide"><span class="mi">history</span>${t('live.lastDays', { n: days })}</button>` : `<button type="button" class="pg-btn" data-action="side-guide"><span class="mi">calendar_view_day</span>${t('live.guide')}</button>`
        ].filter(Boolean).join('');
        return `<div class="pg-head">${logo}<div class="pg-chname">${escapeHtml(cleanName(ch.name))}</div><span class="badge-live">${t('common.live')}</span></div>
            ${cur ? row(cur, 'now') : `<div class="pg-row"><div class="pg-main"><div class="pg-title">${escapeHtml(cleanName(ch.name))}</div><div class="pg-desc">${t('live.noEpg')}</div></div></div>`}
            ${next.map((p) => row(p, 'next')).join('')}
            <div class="pg-actions" data-nav-row>${actions}</div>
            ${this._sourceHtml()}${this._netHtml()}`;
    }

    /** ¿Se puede volver al principio de un programa con lo que hay en memoria (la ventana de pausa)? */
    _canRewindTo(startMs) {
        try {
            if (this.mode !== 'live' || this.catchup) return false;
            const win = this.core.dvrWindow();
            return win > 30 && (Date.now() - startMs) / 1000 < win - 5;
        } catch (e) { return false; }
    }

    /** «Ver desde el inicio» del programa en emisión: con catch-up del proveedor si lo hay y, si no, retrocediendo en la ventana de pausa. */
    _startOver(startMs) {
        const program = epg.schedule(this.item.epgId, { past: 0, future: 1 }).find((p) => p.start === startMs);
        if (program && catalog.canCatchup(this.item, program)) { this._playCatchup(startMs); this.el.info.classList.remove('is-open'); return; }
        if (!this._canRewindTo(startMs)) return;
        const r = this.core.seekRange();
        this.core.seek(Math.max(r.start, r.end - (Date.now() - startMs) / 1000));
        this.el.info.classList.remove('is-open');
        this._wake();
    }

    _renderInfo() {
        const box = this.el.info;
        const guide = this.mode === 'live' && !this.catchup && settings.get('liveInfoStyle') !== 'simple' && this.item && this.item.epgId;
        box.classList.toggle('is-guide', !!guide);
        if (guide) { box.innerHTML = this._liveGuideHtml(); return; }
        if (this.mode === 'live') {
            const nn = epg.nowNext(this.item.epgId);
            if (!nn.now && !nn.next) {
                box.innerHTML = `<div class="player-info-now"><span class="badge-live">${t('common.live')}</span></div><h3>${escapeHtml(cleanName(this.item.name))}</h3><p>${t('live.noEpg')}</p>${this._sourceHtml()}`;
                return;
            }
            const now = nn.now;
            const prog = now ? splitProgramDesc(now.desc) : null;
            box.innerHTML = `
                <div class="player-info-now"><span class="badge-live">${t('common.live')}</span>${now ? escapeHtml(formatClock(now.start) + ' – ' + formatClock(now.stop)) : ''}</div>
                <h3>${escapeHtml(now ? now.title : cleanName(this.item.name))}</h3>
                ${now ? `<div class="epg-bar"><i style="width:${programProgress(now)}%"></i></div>` : ''}
                ${prog ? programMeta(prog) : ''}
                ${prog && prog.plot ? `<p>${escapeHtml(prog.plot)}</p>` : ''}
                ${nn.next ? `<div class="player-info-next">${t('common.next')}: <b>${escapeHtml(formatClock(nn.next.start))} ${escapeHtml(nn.next.title)}</b></div>` : ''}${this._sourceHtml()}${this._netHtml()}`;
        } else if (this.mode === 'vod') {
            box.innerHTML = `<h3>${escapeHtml(stripCountry(this.item.name))}</h3>${this.item.plot ? `<p>${escapeHtml(this.item.plot)}</p>` : ''}${this._sourceHtml()}`;
        } else {
            box.innerHTML = `<h3>T${this.episode.season} E${this.episode.num} · ${escapeHtml(this.episode.title)}</h3>${this.episode.plot ? `<p>${escapeHtml(this.episode.plot)}</p>` : ''}${this._sourceHtml()}`;
        }
    }

    _renderPlayState() {
        const paused = this.core.paused;
        this.el.playbtn.querySelector('.mi').textContent = paused ? 'play_arrow' : 'pause';
        this.el.bigplay.hidden = !paused || !this.el.error.hidden;
        this.el.bigplay.querySelector('.mi').textContent = 'play_arrow';
    }

    _renderTime() {
        const c = this.core;
        const live = this.mode === 'live' && !this.catchup;
        const range = c.seekRange();
        let pct = 0;
        let bufPct = 0;
        if (live) {
            const win = c.dvrWindow();
            if (win > 60) {
                pct = ((c.currentTime - range.start) / win) * 100;
                bufPct = ((c.buffered() - range.start) / win) * 100;
                this.el.time.textContent = c.isAtLiveEdge() ? '' : '-' + formatDuration(range.end - c.currentTime);
                this.el.seek.style.display = '';
            } else {
                this.el.seek.style.display = 'none';
                this.el.time.textContent = formatClock(Date.now());
            }
            this.el.livebtn.classList.toggle('is-live', c.isAtLiveEdge());
        } else {
            const d = c.duration;
            this.el.seek.style.display = '';
            pct = d ? (c.currentTime / d) * 100 : 0;
            bufPct = d ? (c.buffered() / d) * 100 : 0;
            this.el.time.textContent = formatDuration(c.currentTime) + ' / ' + formatDuration(d);
        }
        // Mientras se arrastra, el punto va con el dedo y no con el vídeo.
        const dragging = this.el.seek.classList.contains('is-dragging');
        if (!dragging) this.el.fill.style.width = Math.max(0, Math.min(100, pct)) + '%';
        this.el.buffer.style.width = Math.max(0, Math.min(100, bufPct)) + '%';
        if (!dragging) this.el.thumb.style.left = Math.max(0, Math.min(100, pct)) + '%';
    }

    _formatSeekTime(p) {
        const c = this.core;
        if (this.mode === 'live' && !this.catchup) {
            const win = c.dvrWindow();
            return '-' + formatDuration(win - win * p);
        }
        return formatDuration(c.duration * p);
    }

    _previewSeek(p) {
        this.el.fill.style.width = (p * 100) + '%';
        this.el.thumb.style.left = (p * 100) + '%';
    }

    _commitSeek(p) {
        const c = this.core;
        const r = c.seekRange();
        if (this.mode === 'live' && !this.catchup) {
            c.seek(r.start + c.dvrWindow() * p);
        } else {
            c.seek(c.duration * p);
        }
        this._wake();
    }

    _renderVolume() {
        const muted = this.core.muted || this.core.volume === 0;
        this.el.mutebtn.querySelector('.mi').textContent = muted ? 'volume_off' : this.core.volume < 0.5 ? 'volume_down' : 'volume_up';
        this.el.volume.value = muted ? 0 : Math.round(this.core.volume * 100);
    }

    _renderQuality() {
        const level = this.core.currentLevel();
        const h = level && level.height ? level.height : this.video.videoHeight;
        if (!level && this.mode === 'live' && this._variants && this._variants.length > 1) { this.el.quality.hidden = false; this.el.quality.textContent = variantLabel(this.item) + (this._variantManual ? '' : ' · auto'); return; }
        if (h) {
            this.el.quality.hidden = false;
            this.el.quality.textContent = h + 'p' + (level && level.auto ? ' · auto' : '');
        } else {
            this.el.quality.hidden = true;
        }
    }

    _renderFullscreen() {
        const fs = !!(document.fullscreenElement || document.webkitFullscreenElement);
        this.el.fsicon.textContent = fs ? 'fullscreen_exit' : 'fullscreen';
    }

    _spinner(show) {
        this.el.spinner.hidden = !show;
    }

    _flash(icon, text) {
        const el = this.el.flash;
        el.innerHTML = `<span class="mi">${icon}</span>${text ? `<span>${escapeHtml(text)}</span>` : ''}`;
        el.classList.add('is-open');
        clearTimeout(this.uiTimer);
        this.uiTimer = setTimeout(() => el.classList.remove('is-open'), 900);
    }

    _wake(keep) {
        this.root.classList.remove('is-idle');
        clearTimeout(this.idleTimer);
        if (keep) return;
        this.idleTimer = setTimeout(() => this._sleep(), IDLE_MS);
    }

    _sleep(force) {
        if (!this.isOpen || this.side || !this.el.error.hidden || this.el.next.classList.contains('is-open')) return;
        if (this.core.paused && !force) return;
        this.root.classList.add('is-idle');
        this.el.info.classList.remove('is-open');
    }

    /* ---------------- Panel lateral ---------------- */

    _openSide(kind) {
        this.side = kind;
        this.el.side.classList.add('is-open');
        this.el.side.removeAttribute('data-nav-skip');
        this._wake(true);
        const inEpisodes = kind === 'episodes' && this.mode === 'episode';
        const strip = ((kind === 'channels' && this.mode === 'live') || inEpisodes) && settings.get('channelListStyle') !== 'panel';
        this.el.side.classList.toggle('is-strip', strip);
        if (inEpisodes) this._renderEpisodes(0, strip);
        else if (strip) this._renderChannelStrip();
        else if (kind === 'channels') this._renderChannelSide(this._sideCat || 'all');
        else if (kind === 'guide') this._renderGuideSide();
        else if (kind === 'tracks') this._renderTracksSide();
        else if (kind === 'subs') this._renderSubsSide();
        else this._renderSettingsSide();
        setTimeout(() => {
            const current = this.el.sidebody.querySelector('.is-current') || this.el.sidebody.querySelector('[data-nav-default]');
            if (device.isTV || !device.hasTouch) tvnav.focusLayer(this.el.side, current);
            if (current) current.scrollIntoView({ block: 'center' });
        }, 60);
    }

    _closeSide() {
        this.side = null;
        this.el.side.classList.remove('is-open');
        // La tira de canales se retira cuando acaba su deslizamiento (si no, el panel lateral normal saldría con su aspecto).
        const leaving = this.el.side;
        setTimeout(() => { if (!this.side) leaving.classList.remove('is-strip'); }, 300);
        this.el.side.setAttribute('data-nav-skip', '');
        if (this.isOpen) {
            this._wake();
            if (device.isTV) tvnav.focusLayer(this.root, this.el.playbtn);
        }
    }

    /** Lista de canales en tira inferior (como los operadores): el canal enfocado con su programa y su progreso, y los demás en fila; el directo sigue de fondo. */
    _renderChannelStrip() {
        this.el.sidetitle.textContent = t('player.channels');
        const list = this.channelList.slice(0, 600);
        const tiles = list.map((ch) => {
            const nn = epg.nowNext(ch.epgId);
            const current = this.item && ch.id === this.item.id;
            return `<button type="button" class="ps-tile ${current ? 'is-current' : ''}" data-action="side-channel" data-id="${escapeHtml(String(ch.id))}" ${current ? 'data-nav-default' : ''}>
                <span class="ps-logo ${ch.logo ? '' : 'is-empty'}">${ch.logo ? `<img src="${escapeHtml(imageUrl(ch.logo))}" alt="" loading="lazy" onerror="this.remove()"/>` : `<span class="ps-num">${escapeHtml(String(ch.num || ''))}</span>`}</span>
                <span class="ps-now">${nn.now ? escapeHtml(nn.now.title) : escapeHtml(cleanName(ch.name))}</span>
            </button>`;
        }).join('');
        this.el.sidebody.innerHTML = `<div class="ps-info" data-el-strip-info></div><div class="ps-row" data-nav-row>${tiles}</div>`;
        const info = this.el.sidebody.querySelector('[data-el-strip-info]');
        const paint = (btn) => {
            const ch = btn && (this.channelList.find((c) => String(c.id) === btn.getAttribute('data-id')) || catalog.get('live', btn.getAttribute('data-id')));
            if (!ch) return;
            const nn = epg.nowNext(ch.epgId);
            const cat = (catalog.category('live', ch.categoryId) || {}).name || '';
            const now = nn.now;
            info.innerHTML = `<div class="ps-label">${t(now ? 'live.nowOn' : 'common.live')}</div>
                <div class="ps-focus">
                    <div class="ps-card ${ch.logo ? '' : 'is-empty'}">${ch.logo ? `<img src="${escapeHtml(imageUrl(ch.logo))}" alt="" onerror="this.remove()"/>` : `<span>${escapeHtml(String(ch.num || ''))}</span>`}</div>
                    <div class="ps-meta">
                        <div class="ps-title">${escapeHtml(now ? now.title : cleanName(ch.name))}</div>
                        <div class="ps-sub">${cat ? `<span class="ps-tag">${escapeHtml(cat)}</span>` : ''}<span>${escapeHtml(cleanName(ch.name))}</span></div>
                        ${now ? `<div class="ps-prog"><span>${formatClock(now.start)}</span><span class="ps-bar"><i style="width:${programProgress(now)}%"></i></span><span>${formatClock(now.stop)}</span></div>` : ''}
                    </div>
                </div>`;
        };
        const row = this.el.sidebody.querySelector('.ps-row');
        row.addEventListener('nav:focus', (e) => paint(e.target.closest('.ps-tile')));
        row.addEventListener('mouseover', (e) => { const b = e.target.closest('.ps-tile'); if (b) paint(b); });
        row.addEventListener('focusin', (e) => paint(e.target.closest('.ps-tile')));
        paint(row.querySelector('.is-current') || row.querySelector('.ps-tile'));
    }

    /** Capítulos de la serie que se está viendo, con el mismo aspecto que los canales: tira inferior (o panel lateral si así se prefiere). */
    async _renderEpisodes(season = 0, strip = this.el.side.classList.contains('is-strip')) {
        this.el.sidetitle.textContent = t('common.episodes');
        const series = this.series;
        if (!series) return;
        this.el.sidebody.innerHTML = `<div class="empty"><p>${t('common.loading')}</p></div>`;
        const info = await catalog.seriesInfo(series.id).catch(() => null);
        if (!info || this.side !== 'episodes') return;
        const seasons = (info.seasons || []).filter((x) => x.episodes && x.episodes.length);
        if (!seasons.length) { this.el.sidebody.innerHTML = `<div class="empty"><p>${t('common.noResults')}</p></div>`; return; }
        const numOf = (x) => Number(x.num != null ? x.num : x.season != null ? x.season : x.episodes[0].season) || 0;
        const curSeason = Number(this.episode && this.episode.season) || numOf(seasons[0]);
        const wanted = season || curSeason;
        const sn = seasons.find((x) => numOf(x) === wanted) || seasons[0];
        const chips = seasons.length > 1 ? `<div class="chips ps-seasons" data-nav-row>${seasons.map((x) => `<button type="button" class="chip ${x === sn ? 'is-active' : ''}" data-action="side-season" data-id="${numOf(x)}">${escapeHtml(t('common.season'))} ${numOf(x)}</button>`).join('')}</div>` : '';
        const isCurrent = (ep) => !!this.episode && String(ep.id) === String(this.episode.id);
        const seen = (ep) => catalog.isWatched('episode', ep.id);
        const bar = (ep) => {
            const pr = catalog.getProgress('episode', ep.id);
            const pct = seen(ep) ? 100 : pr && pr.duration ? Math.min(100, Math.round((pr.position / pr.duration) * 100)) : 0;
            return pct > 0 ? `<span class="ps-ep-bar"><i style="width:${pct}%"></i></span>` : '';
        };
        const art = (ep) => ep.thumb ? `<img src="${escapeHtml(imageUrl(ep.thumb))}" alt="" loading="lazy" onerror="this.remove()"/>` : `<span class="ps-num">${escapeHtml(String(ep.num))}</span>`;
        const marks = (ep) => seen(ep) ? '<span class="mi ps-ep-seen">check_circle</span>' : '';
        if (!strip) {
            this.el.sidebody.innerHTML = `${chips ? `<div class="px">${chips}</div>` : ''}<div class="chlist">${sn.episodes.map((ep) => `<button class="chitem ${isCurrent(ep) ? 'is-current' : ''}" data-action="side-episode" data-id="${escapeHtml(String(ep.id))}" ${isCurrent(ep) ? 'data-nav-default' : ''}>
                <span class="chitem-logo">${art(ep)}</span><span class="chitem-body"><span class="chitem-name">${ep.num}. ${escapeHtml(ep.title || '')}</span>${ep.durationSecs ? `<span class="chitem-sub">${formatDuration(ep.durationSecs)}</span>` : ''}</span>${marks(ep)}</button>`).join('')}</div>`;
            return;
        }
        const tiles = sn.episodes.map((ep) => `<button type="button" class="ps-tile is-ep ${isCurrent(ep) ? 'is-current' : ''} ${seen(ep) ? 'is-seen' : ''}" data-action="side-episode" data-id="${escapeHtml(String(ep.id))}" ${isCurrent(ep) ? 'data-nav-default' : ''}>
            <span class="ps-logo ${ep.thumb ? '' : 'is-empty'}">${art(ep)}${marks(ep)}${bar(ep)}</span>
            <span class="ps-now">${ep.num}. ${escapeHtml(ep.title || '')}</span>
        </button>`).join('');
        this.el.sidebody.innerHTML = `${chips}<div class="ps-info" data-el-strip-info></div><div class="ps-row" data-nav-row>${tiles}</div>`;
        const infoEl = this.el.sidebody.querySelector('[data-el-strip-info]');
        const paint = (btn) => {
            const ep = btn && sn.episodes.find((x) => String(x.id) === btn.getAttribute('data-id'));
            if (!ep) return;
            infoEl.innerHTML = `<div class="ps-label">${escapeHtml(series.name || info.name || '')} · ${escapeHtml(t('common.season'))} ${ep.season} · ${escapeHtml(t('common.episodeShort'))}${ep.num}</div>
                <div class="ps-focus">
                    <div class="ps-card is-still ${ep.thumb ? '' : 'is-empty'}">${art(ep)}</div>
                    <div class="ps-meta">
                        <div class="ps-title">${escapeHtml(ep.title || '')}</div>
                        ${ep.durationSecs ? `<div class="ps-sub"><span>${escapeHtml(formatDuration(ep.durationSecs))}</span></div>` : ''}
                        ${ep.plot ? `<div class="ps-plot">${escapeHtml(ep.plot)}</div>` : ''}
                    </div>
                </div>`;
        };
        const row = this.el.sidebody.querySelector('.ps-row');
        row.addEventListener('nav:focus', (e) => paint(e.target.closest('.ps-tile')));
        row.addEventListener('mouseover', (e) => { const b = e.target.closest('.ps-tile'); if (b) paint(b); });
        row.addEventListener('focusin', (e) => paint(e.target.closest('.ps-tile')));
        paint(row.querySelector('.is-current') || row.querySelector('.ps-tile'));
        const cur = row.querySelector('.is-current');
        if (cur) cur.scrollIntoView({ inline: 'center', block: 'nearest' });
    }

    async _playEpisodeById(id) {
        if (this.mode !== 'episode' || !this.series) return;
        const info = await catalog.seriesInfo(this.series.id).catch(() => null);
        const ep = info && (info.seasons || []).reduce((found, sn) => found || sn.episodes.find((x) => String(x.id) === String(id)), null);
        if (!ep) return;
        this._closeSide();
        if (this.episode && String(ep.id) === String(this.episode.id)) return;
        this._saveProgress(true);
        this.episode = ep;
        this._cancelNextCountdown();
        this._loadCurrent(0);
        this.emit('change', this.episode);
    }

    _renderChannelSide(catId) {
        this._sideCat = catId;
        this.el.sidetitle.textContent = t('player.channels');
        // Solo las categorías con canales en la lista con la que se abrió el reproductor (los filtros de «TV en directo»).
        const present = new Set(this.channelList.map((c) => String(c.categoryId)));
        const cats = catalog.categories.live.filter((c) => present.has(String(c.id)));
        if (catId !== 'all' && !present.has(String(catId))) catId = 'all';
        const list = catId === 'all' ? this.channelList : this.channelList.filter((c) => String(c.categoryId) === String(catId));
        const chips = `<div class="chips" data-nav-row>
            <button class="chip ${catId === 'all' ? 'is-active' : ''}" data-action="side-cat" data-id="all">${t('common.all')}</button>
            ${cats.map((c) => `<button class="chip ${catId === c.id ? 'is-active' : ''}" data-action="side-cat" data-id="${c.id}">${escapeHtml(c.name)}</button>`).join('')}
        </div>`;
        const items = list.slice(0, 600).map((ch) => {
            const nn = epg.nowNext(ch.epgId);
            const current = this.item && ch.id === this.item.id;
            return `<button class="chitem ${current ? 'is-current' : ''}" data-action="side-channel" data-id="${ch.id}" ${current ? 'data-nav-default' : ''}>
                <span class="chitem-num">${ch.num}</span>
                <span class="chitem-logo ${ch.logo ? '' : 'is-empty'}">${ch.logo ? `<img src="${escapeHtml(ch.logo)}" alt="" loading="lazy"/>` : ''}</span>
                <span class="chitem-body">
                    <span class="chitem-name">${escapeHtml(cleanName(ch.name))}</span>
                    ${nn.now ? `<span class="chitem-epg">${escapeHtml(nn.now.title)}</span><span class="chitem-bar"><i style="width:${programProgress(nn.now)}%"></i></span>` : ''}
                </span>
            </button>`;
        }).join('');
        this.el.sidebody.innerHTML = `
            ${chips}
            ${this.item && this.item.epgId ? `<div class="px"><button class="btn btn-ghost btn-block mt-2" data-action="side-guide"><span class="mi">calendar_view_day</span>${t('live.guide')}</button></div>` : ''}
            <div class="chlist mt-2">${items || `<div class="empty"><p>${t('common.noResults')}</p></div>`}</div>`;
        // El icono de relleno del logo se oculta al cargar la imagen (y se retira si falla).
        this.el.sidebody.querySelectorAll('.chitem-logo img').forEach((img) => {
            const mark = () => img.parentElement && img.parentElement.classList.add('has-image');
            if (img.complete && img.naturalWidth) mark();
            else {
                img.addEventListener('load', mark, { once: true });
                img.addEventListener('error', () => img.remove(), { once: true });
            }
        });
    }

    _renderGuideSide() {
        this.el.sidetitle.textContent = t('live.guide') + ' · ' + cleanName(this.item.name);
        // «Últimos N días»: con archivo del proveedor, hasta N días de programas pasados a la vista.
        const days = Number(this.item.archive ? this.item.archiveDays || 1 : 1);
        const list = epg.schedule(this.item.epgId, { past: days > 1 ? Math.min(400, days * 24) : 12, future: 40 });
        const now = Date.now();
        const account = window.IPTV.account;
        const canRecord = !!(account && account.canRecord && this.item && this.item.id != null);
        if (canRecord) this._loadScheduledRecordings();
        this.el.sidebody.innerHTML = list.length ? `<div class="epg-list">${list.map((p) => {
            const isNow = p.start <= now && p.stop > now;
            const past = p.stop <= now;
            const canCatch = catalog.canCatchup(this.item, p) && (past || isNow);
            const scheduled = canRecord && scheduledRecs.has(this._recKey(p));
            const canRec = canRecord && !past && !scheduled;
            const actions = [
                canCatch ? `<button class="btn btn-ghost epg-catchup" data-action="side-catchup" data-start="${p.start}"><span class="mi">history</span>${t('live.catchup')}</button>` : '',
                canRec ? `<button class="btn btn-ghost epg-catchup epg-rec" data-action="side-record" data-start="${p.start}"><span class="mi">fiber_manual_record</span>${t('rec.record')}</button>` : '',
                scheduled ? `<span class="epg-scheduled"><span class="mi">event_available</span>${t('rec.status.scheduled')}</span>` : ''
            ].filter(Boolean);
            return `<div class="epg-item ${isNow ? 'is-now' : ''} ${past ? 'is-past' : ''}" ${canCatch || canRec ? '' : 'tabindex="0"'}>
                <div class="epg-time">${formatClock(p.start)}</div>
                <div>
                    <div class="epg-title">${escapeHtml(p.title)}</div>
                    ${splitProgramDesc(p.desc).plot ? `<div class="epg-desc">${escapeHtml(splitProgramDesc(p.desc).plot)}</div>` : ''}
                    ${actions.length ? `<div class="epg-actions">${actions.join('')}</div>` : ''}
                </div>
            </div>`;
        }).join('')}</div>` : `<div class="empty"><span class="mi">event_busy</span><p>${t('live.noEpg')}</p></div>`;
    }

    /** Clave de una grabación de este canal: canal + fin previsto (con el margen del servidor). */
    _recKey(p) {
        const L = (window.IPTV.account && window.IPTV.account.recordings) || {};
        return this.item.id + ':' + (p.stop + (L.endMarginSec || 0) * 1000);
    }

    /** Marca en la lista los programas ya programados (una consulta por apertura de la guía). */
    _loadScheduledRecordings() {
        if (this._recLoadedAt && Date.now() - this._recLoadedAt < 30000) return;
        this._recLoadedAt = Date.now();
        window.IPTV.account.listRecordings().then((r) => {
            const before = scheduledRecs.size;
            (r.recordings || []).forEach((j) => { if (j.status === 'scheduled' || j.status === 'recording') scheduledRecs.add(j.channelId + ':' + j.endAt); });
            if (scheduledRecs.size !== before && this.side === 'guide') this._renderGuideSide();
        }).catch(() => {});
    }

    /** Programar la grabación de un programa desde la guía lateral del reproductor. */
    /**
     * Grabar en el servidor gasta una conexión más del proveedor (ver el canal aquí ya usa una). Si la cuenta las tiene todas
     * ocupadas, se avisa antes: la grabación esperaría a que quede una libre. Devuelve false si la persona lo deja.
     */
    _recordConnOk(ch) {
        let sub = null;
        try { sub = catalog.subscriptionOfSource((ch && ch.source) || catalog.id); } catch (e) { sub = null; }
        if (!sub || !sub.max || sub.active < sub.max) return Promise.resolve(true);
        return new Promise((resolve) => notify.confirm(t('rec.connWarn', { active: sub.active, max: sub.max }), t('rec.connWarnTitle'), () => resolve(true), () => resolve(false)));
    }

    async _recordProgram(start) {
        const account = window.IPTV.account;
        const ch = this.item;
        const p = epg.schedule(ch.epgId, { past: 12, future: 40 }).find((q) => q.start === start);
        if (!p || !account.canRecord) return;
        const L = account.recordings || {};
        const now = Date.now();
        if (p.start <= now && !(await this._recordConnOk(ch))) return; // las programadas para más tarde no gastan conexión ahora
        try {
            await account.scheduleRecording({ channelId: ch.id, channelName: cleanName(ch.name), url: catalog.rawLiveUrl(ch), title: p.title, startAt: Math.max(now, p.start - (L.startMarginSec || 0) * 1000), endAt: p.stop + (L.endMarginSec || 0) * 1000 });
            scheduledRecs.add(this._recKey(p));
            notify.toast(p.start <= now ? t('rec.started', { title: p.title }) : t('rec.scheduled', { title: p.title, time: formatClock(p.start) }), { icon: p.start <= now ? 'fiber_manual_record' : 'event_available' });
            this._renderGuideSide();
            this._renderRecordButton();
        } catch (e) {
            notify.error(t('rec.error.' + (e.code || 'generic')));
        }
    }

    /** Grupo «Intro» del panel de audio y subtítulos (solo en episodios): marcar el inicio y el final, o quitar la marca. */
    _introMenuHtml() {
        const key = this._seriesKey();
        const mark = getIntro(storage, key);
        const now = formatDuration(this.core.currentTime || 0);
        return `<div class="player-menu-title">${t('intro.title')}</div>
            <p class="px small muted">${mark ? t('intro.current', { start: formatDuration(mark.start), end: formatDuration(mark.end) }) : t('intro.help')}</p>
            <button data-action="intro-start"><span class="mi">flag</span>${t('intro.markStart', { time: now })}${this._introDraft ? ` <small>(${formatDuration(this._introDraft.start)})</small>` : ''}</button>
            <button data-action="intro-end"><span class="mi">outlined_flag</span>${t('intro.markEnd', { time: now })}</button>
            ${mark ? `<button data-action="intro-clear"><span class="mi">delete</span>${t('intro.clear')}</button>` : ''}
            <p class="px small muted">${t('player.rememberHint')}</p>`;
    }

    _renderTracksSide() {
        this.el.sidetitle.textContent = t('player.audio') + ' / ' + t('player.subtitles');
        const audio = this.core.audioTracks();
        const subs = this.core.subtitleTracks();
        const ext = this.subs;
        const anySub = !!ext || subs.some((s) => s.active);
        const canSearch = this.mode !== 'live' && !!this._subsTarget() && window.IPTV.account.subtitles;
        const shift = (v) => (v > 0 ? '+' : '−') + Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' s';
        this.el.sidebody.innerHTML = `
            <div class="player-menu">
                <div class="player-menu-title">${t('player.audio')}</div>
                ${audio.length ? audio.map((a) => `<button class="${a.active ? 'is-active' : ''}" data-action="set-audio" data-index="${a.index}"><span class="mi">check</span>${escapeHtml(a.label)}</button>`).join('') : `<button disabled><span class="mi"></span>${t('common.auto')}</button>`}
                <div class="player-menu-title">${t('player.subtitles')}</div>
                <button class="${anySub ? '' : 'is-active'}" data-action="set-sub" data-index="-1" data-nav-default><span class="mi">check</span>${t('player.off')}</button>
                ${subs.map((s) => `<button class="${!ext && s.active ? 'is-active' : ''}" data-action="set-sub" data-index="${s.index}"><span class="mi">check</span>${escapeHtml(s.label)}</button>`).join('')}
                ${ext ? `<button class="is-active" data-action="subs-search"><span class="mi">check</span><span class="player-menu-text">${escapeHtml(subLangName(ext.lang))}<small>${escapeHtml(ext.label)}</small></span></button>
                <div class="player-menu-title">${t('subs.sync')}</div>
                <div class="player-sync" data-nav-row>
                    <button data-action="subs-shift" data-value="-0.5" aria-label="${t('subs.earlier')}"><span class="mi">remove</span></button>
                    <span class="player-sync-value">${ext.offset ? shift(ext.offset) : t('subs.inSync')}</span>
                    <button data-action="subs-shift" data-value="0.5" aria-label="${t('subs.later')}"><span class="mi">add</span></button>
                </div>
                <p class="px small muted">${t('subs.syncHint')}</p>` : ''}
                ${canSearch ? `<button data-action="subs-search"><span class="mi is-shown">travel_explore</span>${t(ext ? 'subs.searchOther' : 'subs.search')}</button>` : ''}
                ${this._seriesKey() ? this._introMenuHtml() : ''}
            </div>`;
    }

    /* ---------------- Subtítulos externos ---------------- */

    /** Título al que pertenecen los subtítulos; null en directo y en grabaciones. */
    _subsTarget() {
        if (this.mode === 'live') return null;
        const target = this.mode === 'vod' ? this.item : this.episode;
        if (!target || target.id == null || /^rec:/.test(String(target.id))) return null;
        const kind = this.mode === 'vod' ? 'vod' : 'episode';
        return { kind, id: String(target.id), key: subtitleStore.key(catalog.id, kind, target.id) };
    }

    async _subsQuery(lang) {
        const bare = (name) => cleanName(name || '').replace(/\s*[([]\s*(19|20)\d{2}\s*[)\]]\s*/g, ' ').trim();
        if (this.mode === 'vod') {
            const info = (await catalog.vodInfo(this.item.id).catch(() => null)) || this.item;
            return { kind: 'vod', tmdb: info.tmdb || '', title: bare(info.name), year: info.year || '', lang };
        }
        const series = this.series || {};
        const known = catalog.peekInfo('series', series.id) || series;
        return { kind: 'episode', tmdb: known.tmdb || series.tmdb || '', title: bare(series.name), season: this.episode.season, episode: this.episode.num, lang };
    }

    /** Al empezar un título: el subtítulo que se eligió la otra vez o, si está activado, el mejor que haya. */
    async _restoreSubs() {
        this._clearSubs();
        const target = this._subsTarget();
        if (!target) return;
        const token = this._subsToken;
        const saved = await subtitleStore.get(target.key);
        if (token !== this._subsToken || !this.isOpen) return;
        if (saved && saved.text) {
            this._applySubs(saved, target);
            return;
        }
        const account = window.IPTV.account;
        if (!settings.get('subtitlesAuto') || !account.subtitles || navigator.onLine === false) return;
        try {
            const lang = settings.get('subtitleLang') || 'es';
            const list = await account.subtitleSearch(await this._subsQuery(lang));
            const best = list.find((x) => !x.machine);
            if (!best || token !== this._subsToken || !this.isOpen) return;
            await this._loadSubs(best, target, token);
        } catch (e) { /* sin subtítulos: se reproduce igual */ }
    }

    async _loadSubs(choice, target, token) {
        const file = await window.IPTV.account.subtitleFile(choice.id);
        if ((token && token !== this._subsToken) || !this.isOpen || !file || !file.text) return false;
        const current = this._subsTarget();
        if (!current || current.key !== target.key) return false;
        const saved = { fileId: choice.id, lang: choice.lang, label: choice.release || file.name || '', offset: 0, text: file.text };
        if (!this._applySubs(saved, target)) return false;
        subtitleStore.set(target.key, saved);
        return true;
    }

    _applySubs(saved, target) {
        const cues = parseSubtitles(saved.text);
        if (!cues.length) return false;
        this.core.setSubtitleTrack(-1);
        this.subs = { key: target.key, fileId: saved.fileId, lang: saved.lang, label: saved.label, offset: Number(saved.offset) || 0, text: saved.text, cues, shown: '' };
        clearInterval(this._subsTimer);
        // timeupdate llega unas 4 veces por segundo; el temporizador afina la entrada y la salida.
        this._subsTimer = setInterval(() => this._renderSubs(), 120);
        this._renderSubs();
        return true;
    }

    _clearSubs({ forget = false } = {}) {
        clearInterval(this._subsTimer);
        this._subsTimer = null;
        if (forget && this.subs) subtitleStore.remove(this.subs.key);
        this.subs = null;
        this._subsToken = (this._subsToken || 0) + 1;
        if (this.el.subs) this.el.subs.innerHTML = '';
    }

    _renderSubs() {
        const subs = this.subs;
        if (!subs || !this.isOpen) return;
        const html = cuesAt(subs.cues, this.core.currentTime - subs.offset).map((c) => `<span>${cueHtml(c.text)}</span>`).join('');
        if (html === subs.shown) return;
        subs.shown = html;
        this.el.subs.innerHTML = html;
    }

    /** Adelanta (negativo) o retrasa (positivo) los subtítulos; se recuerda para este título. */
    _shiftSubs(delta) {
        const subs = this.subs;
        if (!subs) return;
        subs.offset = Math.round((subs.offset + delta) * 10) / 10;
        subtitleStore.set(subs.key, { fileId: subs.fileId, lang: subs.lang, label: subs.label, offset: subs.offset, text: subs.text });
        this._renderSubs();
        this._renderTracksSide();
        const again = this.el.sidebody.querySelector(`[data-action="subs-shift"][data-value="${delta}"]`);
        if (again && (device.isTV || !device.hasTouch)) tvnav.focus(again);
    }

    async _renderSubsSide() {
        const lang = settings.get('subtitleLang') || 'es';
        this.el.sidetitle.textContent = t('subs.title');
        const head = `<button data-action="subs-back"><span class="mi is-shown">arrow_back</span>${t('player.audio')} / ${t('player.subtitles')}</button>
            <div class="player-menu-title">${t('subs.language')}</div>
            <div class="player-langs" data-nav-row>${SUB_LANGS.map((l) => `<button class="${l[0] === lang ? 'is-active' : ''}" data-action="subs-lang" data-value="${l[0]}">${l[1]}</button>`).join('')}</div>
            <div class="player-menu-title">${t('subs.results')}</div>`;
        const paint = (body) => { if (this.side === 'subs') this.el.sidebody.innerHTML = `<div class="player-menu player-menu-subs">${head}${body}</div>`; };
        paint('<div class="spinner"></div>');
        const token = (this._subsSearch = (this._subsSearch || 0) + 1);
        let list;
        try {
            list = await window.IPTV.account.subtitleSearch(await this._subsQuery(lang));
        } catch (e) {
            if (token === this._subsSearch) paint(`<p class="px small muted">${t(navigator.onLine === false ? 'common.offline' : 'subs.errorSearch')}</p>`);
            return;
        }
        if (token !== this._subsSearch) return;
        this._subsResults = list;
        if (!list.length) {
            paint(`<p class="px small muted">${t('subs.none', { lang: subLangName(lang) })}</p>`);
            return;
        }
        const active = this.subs ? this.subs.fileId : '';
        paint(list.map((x, i) => {
            const tags = [x.downloads ? t('subs.downloads', { n: x.downloads.toLocaleString() }) : '', x.hearingImpaired ? t('subs.hearingImpaired') : '', x.machine ? t('subs.machine') : ''].filter(Boolean).join(' · ');
            return `<button class="${x.id === active ? 'is-active' : ''}" data-action="subs-pick" data-id="${escapeHtml(x.id)}" ${i === 0 ? 'data-nav-default' : ''}><span class="mi">check</span><span class="player-menu-text">${escapeHtml(x.release || subLangName(x.lang))}<small>${escapeHtml(tags)}</small></span></button>`;
        }).join(''));
    }

    async _pickSubs(id) {
        const target = this._subsTarget();
        const choice = (this._subsResults || []).find((x) => x.id === id);
        if (!target || !choice) return;
        const btn = this.el.sidebody.querySelector(`[data-action="subs-pick"][data-id="${CSS.escape(id)}"]`);
        if (btn) btn.classList.add('is-loading');
        try {
            const ok = await this._loadSubs(choice, target, 0);
            if (!ok) throw new Error('empty');
            this._tracksTouched = true;
            if (choice.lang) this._rememberTracks({ subs: choice.lang });
            notify.toast(t('subs.loaded'), { icon: 'subtitles', duration: 1800 });
            if (this.side === 'subs') this._openSide('tracks');
        } catch (e) {
            if (btn) btn.classList.remove('is-loading');
            notify.error(t(e && e.code === 'subtitles_quota' ? 'subs.errorQuota' : 'subs.errorLoad'));
        }
    }

    _renderSettingsSide() {
        this.el.sidetitle.textContent = t('player.settings');
        const levels = this.core.levels();
        const current = this.core.currentLevel();
        const auto = !current || current.auto;
        const rate = this.core.playbackRate;
        this.el.sidebody.innerHTML = `
            <div class="player-menu">
                ${this._variantMenuHtml(levels)}
                ${levels.length > 1 ? `<div class="player-menu-title">${t('player.quality')}</div>
                <button class="${auto ? 'is-active' : ''}" data-action="set-level" data-index="-1" data-nav-default><span class="mi">check</span>${t('common.auto')}</button>
                ${levels.slice().sort((a, b) => b.height - a.height).map((l) => `<button class="${!auto && current.index === l.index ? 'is-active' : ''}" data-action="set-level" data-index="${l.index}"><span class="mi">check</span>${escapeHtml(l.label)}</button>`).join('')}` : ''}
                ${this.mode !== 'live' ? `<div class="player-menu-title">${t('player.speed')}</div>
                ${SPEEDS.map((s) => `<button class="${rate === s ? 'is-active' : ''}" data-action="set-speed" data-value="${s}"><span class="mi">check</span>${s}x</button>`).join('')}` : ''}
                <div class="player-menu-title">${t('player.fit')}</div>
                <button class="${this.fit === 'contain' ? 'is-active' : ''}" data-action="set-fit" data-value="contain"><span class="mi">check</span>${t('player.fitContain')}</button>
                <button class="${this.fit === 'cover' ? 'is-active' : ''}" data-action="set-fit" data-value="cover"><span class="mi">check</span>${t('player.fitCover')}</button>
                ${this.mode !== 'live' && transcodeAvailable() ? `<div class="player-menu-title">${t('player.audio')}</div>
                <button class="${this.core.isTranscoding ? 'is-active' : ''}" data-action="toggle-transcode"><span class="mi">${this.core.isTranscoding ? 'check' : ''}</span>${t('player.transcode')}</button>` : ''}
                <div class="player-menu-title">${t('settings.diagnostics')}</div>
                <button class="${this.statsOn ? 'is-active' : ''}" data-action="toggle-stats"><span class="mi">${this.statsOn ? 'check' : ''}</span>${t('player.stats')}</button>
                <button disabled><span class="mi"></span>${this.core.engineName()}</button>
                <p class="px small muted tv-only">${t('player.keysHint')}</p>
            </div>`;
    }

    /* ---------------- Estadísticas ---------------- */

    _toggleStats() {
        this.statsOn = !this.statsOn;
        this.el.stats.hidden = !this.statsOn;
        clearInterval(this._statsTimer);
        if (this.statsOn) {
            this._renderStats();
            this._statsTimer = setInterval(() => this._renderStats(), 1000);
        }
    }

    _renderStats() {
        if (!this.isOpen) return;
        const st = this.core.stats();
        const d = this.core.diagnostics();
        const lines = [
            this._sourceText() ? `${t('detail.provider')}: ${this._sourceText()}` : '',
            `${st.engine}${st.codec ? ' · ' + st.codec : ''}${st.size ? ' · ' + st.size : ''}`,
            `buffer ${st.buffer.toFixed(1)}s${st.latency != null ? ' · latency ' + st.latency.toFixed(1) + 's' : ''}`,
            `${st.bitrate ? 'stream ' + st.bitrate + ' kb/s · ' : ''}${st.bandwidth ? 'net ~' + st.bandwidth + ' kb/s' : this._netEstimateKbps() ? 'net ~' + this._netEstimateKbps() + ' kb/s (last)' : ''}`,
            `frames ${st.frames} · dropped ${st.dropped}`,
            this.lastStartupMs ? `arranque ${this.lastStartupMs} ms${this.startups && this.startups.length > 1 ? ' · media ' + Math.round(this.startups.reduce((a, b) => a + b, 0) / this.startups.length) + ' ms (' + this.startups.length + ')' : ''}` : '',
            `${d.host || ''} · ${device.platformLabel()}${navigator.connection && navigator.connection.effectiveType ? ' · ' + navigator.connection.effectiveType : ''}`
        ].filter((l) => l.trim() !== '·' && l.trim());
        this.el.stats.textContent = lines.join('\n');
    }

    /* ---------------- Wake lock ---------------- */

    async _requestWakeLock() {
        try {
            if ('wakeLock' in navigator && !this.wakeLock) {
                this.wakeLock = await navigator.wakeLock.request('screen');
                this.wakeLock.addEventListener('release', () => { this.wakeLock = null; });
            }
        } catch (e) { /* no soportado o sin permiso */ }
        if (window.plugins && window.plugins.insomnia) {
            try { window.plugins.insomnia.keepAwake(); } catch (e) { /* ignore */ }
        }
    }

    _releaseWakeLock() {
        if (this.wakeLock) {
            this.wakeLock.release().catch(() => {});
            this.wakeLock = null;
        }
        if (window.plugins && window.plugins.insomnia) {
            try { window.plugins.insomnia.allowSleepAgain(); } catch (e) { /* ignore */ }
        }
    }
}
